import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ImageSelector } from '../components/ImageSelector';
import {
    Shield,
    Users,
    Crown,
    BookOpen,
    Calendar,
    Plus,
    Trash2,
    Edit,
    Save,
    X,
    Loader2,
    ChefHat,
    DollarSign,
    ShoppingCart,
    Copy,
    Lightbulb,
    Tag,
    Check,
    Link2,
    XCircle,
    Clock,
    AlertCircle,
    Mail,
    Bell,
    Sparkles,
    Wand2,
    Database,
    Download,
    Upload,
    History,
    ListChecks,
    Snowflake,
    Eye,
    EyeOff,
    ChevronDown,
    BarChart3,
    TrendingUp,
    Settings,
    Activity,
    Smartphone,
    Tablet,
    Monitor,
    Globe,
    RefreshCw,
    Search,
    Heart,
    UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

// Backup Button Component
const BackupButton = () => {
    const [showBackupModal, setShowBackupModal] = useState(false);
    const [backups, setBackups] = useState([]);
    const [loadingBackups, setLoadingBackups] = useState(false);
    const [creatingBackup, setCreatingBackup] = useState(false);
    const [restoring, setRestoring] = useState(null);

    const loadBackups = async () => {
        setLoadingBackups(true);
        try {
            const response = await api.listBackups();
            setBackups(response.data.backups || []);
        } catch (error) {
            toast.error('Erreur lors du chargement des backups');
        } finally {
            setLoadingBackups(false);
        }
    };

    const handleCreateBackup = async () => {
        setCreatingBackup(true);
        try {
            const response = await api.createBackup();
            toast.success(`Backup créé: ${response.data.filename}`);
            loadBackups();
        } catch (error) {
            toast.error('Erreur lors de la création du backup');
        } finally {
            setCreatingBackup(false);
        }
    };

    const handleRestore = async (filename) => {
        if (!window.confirm(`Restaurer le backup ${filename}?\n\nCette action remplacera toutes les données actuelles.`)) {
            return;
        }
        setRestoring(filename);
        try {
            const response = await api.restoreBackup(filename);
            toast.success(`Données restaurées: ${JSON.stringify(response.data.restored)}`);
            window.location.reload();
        } catch (error) {
            toast.error('Erreur lors de la restauration');
        } finally {
            setRestoring(null);
        }
    };

    return (
        <>
            <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { setShowBackupModal(true); loadBackups(); }}
                className="border-blue-300 text-blue-600 hover:bg-blue-50"
            >
                <Database className="w-4 h-4 mr-1" />
                Backups
            </Button>

            {showBackupModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-heading font-bold text-xl flex items-center gap-2">
                                <Database className="w-5 h-5 text-blue-600" />
                                Sauvegardes
                            </h3>
                            <Button variant="ghost" size="sm" onClick={() => setShowBackupModal(false)}>
                                <X className="w-5 h-5" />
                            </Button>
                        </div>

                        <Button 
                            onClick={handleCreateBackup} 
                            disabled={creatingBackup}
                            className="w-full mb-4 bg-blue-600 text-white hover:bg-blue-700"
                        >
                            {creatingBackup ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Download className="w-4 h-4 mr-2" />
                            )}
                            Créer un backup maintenant
                        </Button>

                        <div className="space-y-2">
                            <h4 className="font-medium text-sm text-gray-500 flex items-center gap-1">
                                <History className="w-4 h-4" />
                                Backups disponibles
                            </h4>
                            
                            {loadingBackups ? (
                                <div className="text-center py-4">
                                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                                </div>
                            ) : backups.length === 0 ? (
                                <p className="text-gray-500 text-sm text-center py-4">Aucun backup disponible</p>
                            ) : (
                                backups.map((backup) => (
                                    <div key={backup.filename} className="border rounded-lg p-3 flex items-center justify-between">
                                        <div>
                                            <p className="font-mono text-sm">{backup.filename}</p>
                                            {backup.counts && (
                                                <p className="text-xs text-gray-500">
                                                    {backup.counts.recipes} recettes, {backup.counts.tips} astuces
                                                </p>
                                            )}
                                        </div>
                                        <Button 
                                            size="sm" 
                                            variant="outline"
                                            onClick={() => handleRestore(backup.filename)}
                                            disabled={restoring === backup.filename}
                                            className="text-green-600 border-green-300 hover:bg-green-50"
                                        >
                                            {restoring === backup.filename ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Upload className="w-4 h-4" />
                                            )}
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>

                        <p className="text-xs text-gray-400 mt-4 text-center">
                            Les 10 derniers backups sont conservés automatiquement
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};

export const AdminPage = () => {
    const { user, loading: authLoading, isAdmin: contextIsAdmin } = useAuth();
    const navigate = useNavigate();
    
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState('stats');
    
    // Data states
    const [packs, setPacks] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [tips, setTips] = useState([]);
    const [deals, setDeals] = useState([]);
    const [dossiers, setDossiers] = useState([]);
    const [users, setUsers] = useState([]);
    
    // Form states
    const [editingPack, setEditingPack] = useState(null);
    const [showNewPackForm, setShowNewPackForm] = useState(false);
    const [duplicatingPack, setDuplicatingPack] = useState(null);
    const [editingGroceryList, setEditingGroceryList] = useState(null); // For grocery list editing modal
    
    const [editingRecipe, setEditingRecipe] = useState(null);
    const [showNewRecipeForm, setShowNewRecipeForm] = useState(false);
    const [adminRecipeSearch, setAdminRecipeSearch] = useState('');
    
    const [editingTip, setEditingTip] = useState(null);
    const [showNewTipForm, setShowNewTipForm] = useState(false);
    
    const [editingDeal, setEditingDeal] = useState(null);
    const [showNewDealForm, setShowNewDealForm] = useState(false);
    const [showAIExtractForm, setShowAIExtractForm] = useState(false);
    const [extractingDeals, setExtractingDeals] = useState(false);
    const [extractedDeals, setExtractedDeals] = useState([]);
    
    const [editingDossier, setEditingDossier] = useState(null);
    const [showNewDossierForm, setShowNewDossierForm] = useState(false);
    
    // AI Generation states
    const [showAIRecipeGenerator, setShowAIRecipeGenerator] = useState(false);
    const [showAITipGenerator, setShowAITipGenerator] = useState(false);
    const [showImportRecipeModal, setShowImportRecipeModal] = useState(false);
    const [importingRecipe, setImportingRecipe] = useState(false);
    const [importUrl, setImportUrl] = useState('');
    const [aiGeneratedRecipes, setAiGeneratedRecipes] = useState([]);
    const [aiGeneratedTips, setAiGeneratedTips] = useState([]);
    const [generatingRecipes, setGeneratingRecipes] = useState(false);
    const [generatingTips, setGeneratingTips] = useState(false);
    const [generatingRecommendations, setGeneratingRecommendations] = useState(false);
    const [selectedAIRecipe, setSelectedAIRecipe] = useState(null);
    const [selectedAITip, setSelectedAITip] = useState(null);
    const [publishingPack, setPublishingPack] = useState(null);
    const [togglingDealPublish, setTogglingDealPublish] = useState(null);
    const [publishingAllDeals, setPublishingAllDeals] = useState(false);
    
    // User management states
    const [togglingPremium, setTogglingPremium] = useState(null);
    const [togglingAdmin, setTogglingAdmin] = useState(null);
    
    // Grocery list editor states
    const [showGroceryEditor, setShowGroceryEditor] = useState(false);
    const [editingGroceryPackId, setEditingGroceryPackId] = useState(null);
    const [groceryListData, setGroceryListData] = useState({ grocery_list: [], available_deals: [] });
    const [savingGroceryList, setSavingGroceryList] = useState(false);

    // Import recipe from URL
    const handleImportRecipe = async () => {
        if (!importUrl.trim()) {
            toast.error('Veuillez entrer une URL');
            return;
        }
        
        setImportingRecipe(true);
        try {
            const response = await api.importRecipeFromUrl(importUrl);
            toast.success(`Recette "${response.data.recipe.title}" importée avec succès!`);
            setShowImportRecipeModal(false);
            setImportUrl('');
            // Show the imported recipe for editing
            setSelectedAIRecipe(response.data.recipe);
            loadAdminData();
        } catch (error) {
            console.error('Import error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
        } finally {
            setImportingRecipe(false);
        }
    };

    // Open grocery list editor
    const handleOpenGroceryEditor = async (packId) => {
        try {
            const response = await api.getGroceryListWithDeals(packId);
            setGroceryListData(response.data);
            setEditingGroceryPackId(packId);
            setShowGroceryEditor(true);
        } catch (error) {
            toast.error('Erreur lors du chargement de la liste');
            console.error(error);
        }
    };

    // Save grocery list
    const handleSaveGroceryList = async () => {
        if (!editingGroceryPackId) return;
        setSavingGroceryList(true);
        try {
            await api.updateGroceryList(editingGroceryPackId, groceryListData.grocery_list);
            toast.success('Liste d\'épicerie sauvegardée !');
            setShowGroceryEditor(false);
            loadAdminData(); // Refresh packs
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
            console.error(error);
        } finally {
            setSavingGroceryList(false);
        }
    };

    // Update grocery item
    const updateGroceryItem = (index, field, value) => {
        setGroceryListData(prev => {
            const newList = [...prev.grocery_list];
            newList[index] = { ...newList[index], [field]: value };
            return { ...prev, grocery_list: newList };
        });
    };

    // Set deal for grocery item
    const setGroceryItemDeal = (index, dealId) => {
        setGroceryListData(prev => {
            const newList = [...prev.grocery_list];
            if (dealId) {
                const deal = prev.available_deals.find(d => d.id === dealId);
                if (deal) {
                    newList[index] = {
                        ...newList[index],
                        has_deal: true,
                        deal_info: {
                            deal_id: deal.id,
                            product_name: deal.product_name,
                            store_name: deal.store_name,
                            original_price: deal.original_price,
                            sale_price: deal.sale_price,
                            discount_percent: deal.discount_percent,
                            savings: Math.round((deal.original_price - deal.sale_price) * 100) / 100,
                            manual: true
                        }
                    };
                }
            } else {
                newList[index] = {
                    ...newList[index],
                    has_deal: false,
                    deal_info: null
                };
            }
            return { ...prev, grocery_list: newList };
        });
    };

    // Add new grocery item
    const addGroceryItem = () => {
        setGroceryListData(prev => ({
            ...prev,
            grocery_list: [...prev.grocery_list, {
                item: '',
                quantity: '1',
                unit: 'unité',
                estimated_cost: 0,
                has_deal: false,
                deal_info: null,
                recipes: []
            }]
        }));
    };

    // Remove grocery item
    const removeGroceryItem = (index) => {
        setGroceryListData(prev => ({
            ...prev,
            grocery_list: prev.grocery_list.filter((_, i) => i !== index)
        }));
    };

    // Duplicate pack function
    const handleDuplicatePack = (pack) => {
        const maxPackNumber = Math.max(...packs.map(p => p.pack_number || p.week_number || 0), 0);
        const nextPackNumber = maxPackNumber + 1;
        
        const duplicatedPack = {
            ...pack,
            id: undefined,
            pack_number: nextPackNumber,
            week_number: nextPackNumber,
            title: `${pack.title} (copie)`,
            created_at: undefined,
            created_by: undefined
        };
        
        setDuplicatingPack(duplicatedPack);
        toast.info(`Duplication du Pack #${pack.pack_number || pack.week_number}`);
    };

    useEffect(() => {
        if (authLoading) return;
        
        if (!user) {
            navigate('/login');
            return;
        }
        
        if (contextIsAdmin) {
            setIsAdmin(true);
            loadAdminData();
        } else {
            toast.error('Accès non autorisé');
            navigate('/');
        }
    }, [user, authLoading, contextIsAdmin, navigate]);

    // Global paste handler for AI extraction form
    useEffect(() => {
        // Only active when AI form is shown and not currently extracting
        if (!showAIExtractForm || extractingDeals) return;
        
        const handleGlobalPaste = async (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            
            for (let item of items) {
                if (item.type.startsWith('image/')) {
                    e.preventDefault();
                    const file = item.getAsFile();
                    if (file) {
                        setExtractingDeals(true);
                        setExtractedDeals([]); // Clear previous results
                        const reader = new FileReader();
                        reader.onload = async (event) => {
                            try {
                                const base64 = event.target.result;
                                const storeHint = document.getElementById('store-hint-select')?.value || '';
                                const response = await api.extractDealsFromImage(base64, storeHint);
                                if (response.data.success) {
                                    setExtractedDeals(response.data.deals);
                                    if (response.data.deals.length === 0) {
                                        toast.info("Aucun rabais détecté dans cette image.");
                                    } else {
                                        toast.success(`${response.data.count} rabais extraits!`);
                                    }
                                } else {
                                    toast.error(response.data.error || "Erreur lors de l'extraction");
                                }
                            } catch (error) {
                                console.error('Error extracting deals:', error);
                                toast.error(error.response?.data?.detail || "Erreur lors de l'extraction des rabais");
                            } finally {
                                setExtractingDeals(false);
                            }
                        };
                        reader.readAsDataURL(file);
                        return;
                    }
                }
            }
        };
        
        document.addEventListener('paste', handleGlobalPaste);
        return () => document.removeEventListener('paste', handleGlobalPaste);
    }, [showAIExtractForm, extractingDeals]);

    const loadAdminData = async () => {
        try {
            const [statsRes, packsRes, recipesRes, tipsRes, dealsRes, dossiersRes, usersRes] = await Promise.all([
                api.getAdminStats(),
                api.getAdminPacks(),
                api.getAdminRecipes(),
                api.getAdminTips(),
                api.getAdminDeals(),
                api.getAdminDossiers(),
                api.getAdminUsers()
            ]);
            setStats(statsRes.data);
            setPacks(packsRes.data);
            setRecipes(recipesRes.data);
            setTips(tipsRes.data);
            setDeals(dealsRes.data);
            setDossiers(dossiersRes.data);
            setUsers(usersRes.data);
        } catch (error) {
            console.error('Error loading admin data:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };
    
    // User handlers
    const [showPremiumModal, setShowPremiumModal] = useState(null); // user id for modal
    const [selectedDuration, setSelectedDuration] = useState(null); // null = unlimited
    
    const handleGivePremium = async (userId, durationDays = null) => {
        setTogglingPremium(userId);
        try {
            const response = await api.giveUserPremium(userId, durationDays);
            toast.success(response.data.message);
            setShowPremiumModal(null);
            setSelectedDuration(null);
            loadAdminData();
        } catch (error) {
            toast.error('Erreur lors de l\'attribution du Premium');
        } finally {
            setTogglingPremium(null);
        }
    };
    
    const handleRevokePremium = async (userId) => {
        if (!window.confirm('Retirer le Premium gratuit de cet utilisateur?')) return;
        setTogglingPremium(userId);
        try {
            await api.revokeUserPremium(userId);
            toast.success('Premium retiré');
            loadAdminData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du retrait du Premium');
        } finally {
            setTogglingPremium(null);
        }
    };

    const handleToggleAdmin = async (userId, currentlyAdmin) => {
        const action = currentlyAdmin ? 'retirer les droits admin de' : 'rendre admin';
        if (!window.confirm(`Voulez-vous ${action} cet utilisateur?`)) return;
        
        setTogglingAdmin(userId);
        try {
            if (currentlyAdmin) {
                await api.removeUserAdmin(userId);
                toast.success('Droits admin retirés');
            } else {
                await api.makeUserAdmin(userId);
                toast.success('Utilisateur promu administrateur');
            }
            loadAdminData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
        } finally {
            setTogglingAdmin(null);
        }
    };

    // Pack handlers
    const handleSavePack = async (packData) => {
        try {
            if (packData.id) {
                await api.updateAdminPack(packData.id, packData);
                setPacks(prev => prev.map(p => p.id === packData.id ? packData : p));
                toast.success('Pack mis à jour');
            } else {
                const response = await api.createAdminPack(packData);
                setPacks(prev => [response.data, ...prev]);
                if (packData.send_notification && response.data.notification_sent) {
                    const notif = response.data.notification_sent;
                    toast.success(`Pack créé! ${notif.sent} email(s) envoyé(s)`);
                } else {
                    toast.success('Pack créé');
                }
            }
            setEditingPack(null);
            setShowNewPackForm(false);
            setDuplicatingPack(null);
            loadAdminData();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const handleSendPackNotification = async (pack) => {
        if (!window.confirm(`Envoyer une notification email pour le Pack #${pack.pack_number || pack.week_number} à tous les membres Premium?`)) return;
        try {
            const response = await api.sendPackNotification(pack.id);
            const result = response.data.result;
            if (result.sent > 0) {
                toast.success(`Notification envoyée à ${result.sent} membre(s) Premium`);
            } else if (result.skipped) {
                toast.error('SendGrid non configuré');
            } else if (result.no_users) {
                toast.info('Aucun membre Premium à notifier');
            } else {
                toast.warning('Aucune notification envoyée');
            }
        } catch (error) {
            toast.error('Erreur lors de l\'envoi des notifications');
        }
    };

    const handleDeletePack = async (packId) => {
        if (!window.confirm('Supprimer ce pack?')) return;
        try {
            await api.deleteAdminPack(packId);
            setPacks(prev => prev.filter(p => p.id !== packId));
            toast.success('Pack supprimé');
            loadAdminData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleTogglePublish = async (packId, isCurrentlyPublished) => {
        const action = isCurrentlyPublished ? 'dépublier' : 'publier';
        if (!window.confirm(`Voulez-vous ${action} ce pack?`)) return;
        
        setPublishingPack(packId);
        try {
            const response = await api.togglePackPublish(packId);
            toast.success(response.data.message);
            loadAdminData();
        } catch (error) {
            toast.error(error.response?.data?.detail || `Erreur lors de la ${action}ation`);
        } finally {
            setPublishingPack(null);
        }
    };

    const handleGenerateGroceryList = async (packId, includeUnpublishedDeals = false) => {
        try {
            const modeText = includeUnpublishedDeals ? ' (avec rabais non publiés)' : '';
            toast.info(`Génération de la liste d'épicerie${modeText}...`);
            const response = await api.generateGroceryList(packId, includeUnpublishedDeals);
            const stats = response.data.stats;
            const unpublishedNote = includeUnpublishedDeals ? ' (prévisualisation admin)' : '';
            toast.success(`Liste générée${unpublishedNote}: ${stats.total_ingredients} articles, ${stats.ingredients_with_deals} en rabais, ${stats.total_savings.toFixed(2)}$ d'économies`);
            loadAdminData();
        } catch (error) {
            console.error('Error generating grocery list:', error);
            if (error.response?.data?.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error('Erreur lors de la génération');
            }
        }
    };

    const handleRefreshDeals = async (packId) => {
        try {
            toast.info('Rafraîchissement des correspondances de rabais...');
            const response = await api.refreshGroceryDeals(packId);
            const stats = response.data.stats;
            toast.success(`Rabais rafraîchis: ${stats.items_with_deals} articles en rabais, ${stats.total_savings.toFixed(2)}$ d'économies`);
            loadAdminData();
        } catch (error) {
            console.error('Error refreshing deals:', error);
            if (error.response?.data?.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error('Erreur lors du rafraîchissement');
            }
        }
    };

    const handleUpdateGroceryItemDeal = async (item, dealId) => {
        if (!editingGroceryList) return;
        
        try {
            await api.updateGroceryItemDeal(editingGroceryList.packId, item, dealId);
            toast.success('Correspondance mise à jour');
            // Refresh the grocery list
            const response = await api.getGroceryListWithDeals(editingGroceryList.packId);
            setEditingGroceryList({
                ...editingGroceryList,
                groceryList: response.data.grocery_list || [],
                totalSavings: response.data.total_savings || 0
            });
            loadAdminData();
        } catch (error) {
            console.error('Error updating deal:', error);
            toast.error('Erreur lors de la mise à jour');
        }
    };

    // AI Generation handlers
    const [aiError, setAiError] = useState(null);
    
    const handleGenerateRecipes = async () => {
        setGeneratingRecipes(true);
        setAiGeneratedRecipes([]);
        setAiError(null);
        try {
            const response = await api.generateRecipes(7);
            if (response.data.parsed && response.data.recipes) {
                setAiGeneratedRecipes(response.data.recipes);
                toast.success(`${response.data.count} recettes générées !`);
            } else {
                toast.error('Erreur de parsing IA');
                console.error('AI Response:', response.data);
            }
        } catch (error) {
            console.error(error);
            const errorMsg = error.response?.data?.detail || error.message || '';
            if (errorMsg.includes('Budget') || errorMsg.includes('budget') || errorMsg.includes('exceeded')) {
                setAiError('budget');
                toast.error('Budget IA dépassé');
            } else {
                setAiError('generic');
                toast.error('Erreur lors de la génération');
            }
        } finally {
            setGeneratingRecipes(false);
        }
    };

    const handleGenerateTips = async () => {
        setGeneratingTips(true);
        setAiGeneratedTips([]);
        setAiError(null);
        try {
            const response = await api.generateTips(7);
            if (response.data.parsed && response.data.tips) {
                setAiGeneratedTips(response.data.tips);
                toast.success(`${response.data.count} astuces générées !`);
            } else {
                toast.error('Erreur de parsing IA');
                console.error('AI Response:', response.data);
            }
        } catch (error) {
            console.error(error);
            const errorMsg = error.response?.data?.detail || error.message || '';
            if (errorMsg.includes('Budget') || errorMsg.includes('budget') || errorMsg.includes('exceeded')) {
                setAiError('budget');
                toast.error('Budget IA dépassé');
            } else {
                setAiError('generic');
                toast.error('Erreur lors de la génération');
            }
        } finally {
            setGeneratingTips(false);
        }
    };

    const handleGenerateRecommendations = async (packId, onSuccess) => {
        if (!packId) {
            toast.error('Veuillez sauvegarder le pack avant de générer les recommandations');
            return;
        }
        setGeneratingRecommendations(true);
        try {
            const response = await api.generateRecommendations(packId);
            if (response.data.recommendations) {
                if (onSuccess) {
                    onSuccess(response.data.recommendations);
                }
                toast.success('Recommandations générées avec succès !');
            }
        } catch (error) {
            console.error(error);
            const errorMsg = error.response?.data?.detail || error.message || '';
            if (errorMsg.includes('Budget') || errorMsg.includes('budget') || errorMsg.includes('crédit') || errorMsg.includes('402')) {
                toast.error('Budget IA insuffisant. Ajoutez des crédits dans Profil → Universal Key.');
            } else {
                toast.error(errorMsg || 'Erreur lors de la génération');
            }
        } finally {
            setGeneratingRecommendations(false);
        }
    };

    const handleSelectAIRecipe = (recipe) => {
        // Transform AI recipe to match RecipeForm format
        const transformedRecipe = {
            title: recipe.title || '',
            description: recipe.description || '',
            category: recipe.category || 'dinner',
            categories: [recipe.category || 'dinner'],
            ingredients: (recipe.ingredients || []).map(ing => ({
                name: ing.name || '',
                quantity: String(ing.quantity || ''),
                unit: ing.unit || 'tasse',
                estimated_cost: String(ing.estimated_cost || ''),
                linked_recipe_id: ''
            })),
            steps: recipe.steps || [''],
            prep_time: recipe.prep_time || 30,
            cook_time: recipe.cook_time || 30,
            servings: recipe.servings || 4,
            cost_per_serving: recipe.cost_per_serving || 0,
            image_url: '',
            tags: Array.isArray(recipe.tags) ? recipe.tags.join(', ') : '',
            difficulty: recipe.difficulty || 'facile',
            is_premium: false,
            solo_tips: ['']
        };
        setSelectedAIRecipe(transformedRecipe);
        setShowAIRecipeGenerator(false);
    };

    const handleSelectAITip = (tip) => {
        // Transform AI tip to match TipForm format
        const transformedTip = {
            title: tip.title || '',
            content: tip.content || '',
            category: tip.category || 'budget',
            savings_estimate: tip.savings_estimate || '',
            is_premium: false
        };
        setSelectedAITip(transformedTip);
        setShowAITipGenerator(false);
    };

    // Recipe handlers
    const handleSaveRecipe = async (recipeData) => {
        try {
            if (recipeData.id) {
                await api.updateAdminRecipe(recipeData.id, recipeData);
                toast.success('Recette mise à jour');
            } else {
                await api.createAdminRecipe(recipeData);
                toast.success('Recette créée');
            }
            setEditingRecipe(null);
            setShowNewRecipeForm(false);
            loadAdminData();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const handleApproveRecipe = async (recipeId) => {
        try {
            await api.approveRecipe(recipeId);
            toast.success('Recette approuvée');
            loadAdminData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleRejectRecipe = async (recipeId) => {
        try {
            await api.rejectRecipe(recipeId);
            toast.success('Recette rejetée');
            loadAdminData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleDeleteRecipe = async (recipeId) => {
        if (!window.confirm('Supprimer cette recette?')) return;
        try {
            await api.deleteAdminRecipe(recipeId);
            toast.success('Recette supprimée');
            loadAdminData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    // Tip handlers
    const handleSaveTip = async (tipData) => {
        try {
            if (tipData.id) {
                await api.updateAdminTip(tipData.id, tipData);
                toast.success('Astuce mise à jour');
            } else {
                await api.createAdminTip(tipData);
                toast.success('Astuce créée');
            }
            setEditingTip(null);
            setShowNewTipForm(false);
            loadAdminData();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const handleApproveTip = async (tipId) => {
        try {
            await api.approveTip(tipId);
            toast.success('Astuce approuvée');
            loadAdminData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleRejectTip = async (tipId) => {
        try {
            await api.rejectTip(tipId);
            toast.success('Astuce rejetée');
            loadAdminData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleDeleteTip = async (tipId) => {
        if (!window.confirm('Supprimer cette astuce?')) return;
        try {
            await api.deleteAdminTip(tipId);
            toast.success('Astuce supprimée');
            loadAdminData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    // Deal handlers
    const handleSaveDeal = async (dealData) => {
        try {
            if (dealData.id) {
                await api.updateAdminDeal(dealData.id, dealData);
                toast.success('Rabais mis à jour');
            } else {
                await api.createAdminDeal(dealData);
                toast.success('Rabais créé');
            }
            setEditingDeal(null);
            setShowNewDealForm(false);
            loadAdminData();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const handleDeleteDeal = async (dealId) => {
        if (!window.confirm('Supprimer ce rabais?')) return;
        try {
            await api.deleteAdminDeal(dealId);
            toast.success('Rabais supprimé');
            loadAdminData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    // AI Deals Extraction
    const handleExtractDealsFromImage = async (imageBase64, storeHint) => {
        setExtractingDeals(true);
        setExtractedDeals([]);
        try {
            const response = await api.extractDealsFromImage(imageBase64, storeHint);
            if (response.data.success) {
                setExtractedDeals(response.data.deals);
                if (response.data.deals.length === 0) {
                    toast.info("Aucun rabais détecté dans cette image. Essayez avec une image plus claire.");
                } else {
                    toast.success(`${response.data.count} rabais extraits!`);
                }
            } else {
                toast.error(response.data.error || "Erreur lors de l'extraction");
            }
        } catch (error) {
            console.error('Error extracting deals:', error);
            toast.error(error.response?.data?.detail || "Erreur lors de l'extraction des rabais");
        } finally {
            setExtractingDeals(false);
        }
    };

    // AI Deals Extraction from PDF
    const handleExtractDealsFromPdf = async (file, storeHint, maxPages = 3) => {
        console.log('handleExtractDealsFromPdf called with file:', file.name, 'maxPages:', maxPages);
        setExtractingDeals(true);
        setExtractedDeals([]);
        try {
            toast.info(`Analyse du PDF en cours (${maxPages} page(s))... Cela peut prendre quelques minutes.`);
            console.log('Sending PDF to API...');
            const response = await api.extractDealsFromPdf(file, storeHint, maxPages);
            console.log('API response received:', response.data);
            if (response.data.success) {
                setExtractedDeals(response.data.deals);
                if (response.data.deals.length === 0) {
                    toast.info("Aucun rabais détecté. Essayez avec moins de pages ou une capture d'écran.");
                } else {
                    toast.success(`${response.data.count} rabais extraits de ${response.data.pages_processed} page(s)!`);
                }
            } else {
                console.error('API returned error:', response.data.error);
                toast.error(response.data.error || "Erreur lors de l'extraction");
            }
        } catch (error) {
            console.error('Error extracting deals from PDF:', error);
            console.error('Error details:', error.response?.data);
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                toast.error("Le traitement a pris trop de temps. Essayez avec moins de pages ou une capture d'écran.");
            } else {
                const errorMessage = error.response?.data?.detail || error.message || "Erreur lors de l'extraction";
                toast.error(errorMessage);
            }
        } finally {
            setExtractingDeals(false);
            console.log('handleExtractDealsFromPdf completed');
        }
    };

    const handleSaveExtractedDeals = async () => {
        if (extractedDeals.length === 0) return;
        try {
            const response = await api.bulkCreateDeals(extractedDeals);
            if (response.data.success) {
                toast.success(`${response.data.created} rabais créés!`);
                setExtractedDeals([]);
                setShowAIExtractForm(false);
                loadAdminData();
            }
        } catch (error) {
            console.error('Error saving deals:', error);
            toast.error("Erreur lors de la sauvegarde des rabais");
        }
    };

    const handleRemoveExtractedDeal = (index) => {
        setExtractedDeals(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpdateExtractedDeal = (index, field, value) => {
        setExtractedDeals(prev => prev.map((deal, i) => 
            i === index ? { ...deal, [field]: value } : deal
        ));
    };

    const handleToggleDealPublish = async (dealId, isCurrentlyPublished) => {
        setTogglingDealPublish(dealId);
        try {
            const response = await api.toggleDealPublish(dealId);
            toast.success(response.data.message);
            loadAdminData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
        } finally {
            setTogglingDealPublish(null);
        }
    };

    const handlePublishAllDeals = async (publish) => {
        const action = publish ? 'publier' : 'dépublier';
        if (!window.confirm(`Voulez-vous ${action} TOUS les rabais?`)) return;
        
        setPublishingAllDeals(true);
        try {
            const response = publish 
                ? await api.publishAllDeals() 
                : await api.unpublishAllDeals();
            toast.success(response.data.message);
            loadAdminData();
        } catch (error) {
            toast.error(error.response?.data?.detail || `Erreur lors de la ${action}ation`);
        } finally {
            setPublishingAllDeals(false);
        }
    };

    // Dossier handlers
    const handleSaveDossier = async (dossierData) => {
        try {
            if (dossierData.id) {
                await api.updateAdminDossier(dossierData.id, dossierData);
                toast.success('Dossier mis à jour');
            } else {
                await api.createAdminDossier(dossierData);
                toast.success('Dossier créé');
            }
            setEditingDossier(null);
            setShowNewDossierForm(false);
            loadAdminData();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const handleDeleteDossier = async (dossierId) => {
        if (!window.confirm('Supprimer ce dossier?')) return;
        try {
            await api.deleteAdminDossier(dossierId);
            toast.success('Dossier supprimé');
            loadAdminData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!isAdmin) return null;

    const pendingRecipes = recipes.filter(r => r.status === 'pending');
    const pendingTips = tips.filter(t => t.status === 'pending');
    const totalPending = pendingRecipes.length + pendingTips.length;

    return (
        <div className="min-h-screen bg-muted/30 py-8" data-testid="admin-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                        <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-foreground">
                            Administration
                        </h1>
                        <p className="text-muted-foreground">
                            Bienvenue, {user?.name}
                        </p>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        {totalPending > 0 && (
                            <Badge variant="destructive">
                                {totalPending} en attente
                            </Badge>
                        )}
                        <BackupButton />
                    </div>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="grid grid-cols-7 w-full max-w-5xl">
                        <TabsTrigger value="stats" className="flex items-center gap-2">
                            <BarChart3 className="w-4 h-4" />
                            Stats
                        </TabsTrigger>
                        <TabsTrigger value="packs" className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Packs
                        </TabsTrigger>
                        <TabsTrigger value="recipes" className="flex items-center gap-2">
                            <ChefHat className="w-4 h-4" />
                            Recettes
                            {pendingRecipes.length > 0 && (
                                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                                    {pendingRecipes.length}
                                </Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="tips" className="flex items-center gap-2">
                            <Lightbulb className="w-4 h-4" />
                            Astuces
                            {pendingTips.length > 0 && (
                                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                                    {pendingTips.length}
                                </Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="deals" className="flex items-center gap-2">
                            <Tag className="w-4 h-4" />
                            Rabais
                        </TabsTrigger>
                        <TabsTrigger value="dossiers" className="flex items-center gap-2">
                            <BookOpen className="w-4 h-4" />
                            Dossiers
                        </TabsTrigger>
                        <TabsTrigger value="users" className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Utilisateurs
                        </TabsTrigger>
                    </TabsList>

                    {/* STATS TAB */}
                    <TabsContent value="stats">
                        <div className="space-y-6">
                            {/* Header */}
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-heading font-bold flex items-center gap-2">
                                    <BarChart3 className="w-6 h-6 text-primary" />
                                    Statistiques du site
                                </h2>
                                <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                                    <TrendingUp className="w-4 h-4 mr-2" />
                                    Actualiser
                                </Button>
                            </div>

                            {stats ? (
                                <div className="grid gap-6">
                                    {/* Users Section */}
                                    <Card className="rounded-2xl">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="font-heading flex items-center gap-2 text-lg">
                                                <Users className="w-5 h-5 text-blue-600" />
                                                Utilisateurs
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div className="p-4 bg-blue-50 rounded-xl text-center">
                                                    <p className="text-3xl font-bold text-blue-700">{stats.users?.total || 0}</p>
                                                    <p className="text-sm text-blue-600">Total</p>
                                                </div>
                                                <div className="p-4 bg-purple-50 rounded-xl text-center">
                                                    <p className="text-3xl font-bold text-purple-700">{stats.users?.premium || 0}</p>
                                                    <p className="text-sm text-purple-600">Premium</p>
                                                </div>
                                                <div className="p-4 bg-green-50 rounded-xl text-center">
                                                    <p className="text-3xl font-bold text-green-700">{stats.users?.premium_receiving_emails || 0}</p>
                                                    <p className="text-sm text-green-600">Reçoivent emails</p>
                                                </div>
                                                <div className="p-4 bg-orange-50 rounded-xl text-center">
                                                    <p className="text-3xl font-bold text-orange-700">{stats.users?.opted_out_emails || 0}</p>
                                                    <p className="text-sm text-orange-600">Désabonnés</p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* Content Section */}
                                    <div className="grid md:grid-cols-2 gap-6">
                                        {/* Recipes */}
                                        <Card className="rounded-2xl">
                                            <CardHeader className="pb-2">
                                                <CardTitle className="font-heading flex items-center gap-2 text-lg">
                                                    <ChefHat className="w-5 h-5 text-green-600" />
                                                    Recettes
                                                </CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="space-y-4">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-muted-foreground">Approuvées</span>
                                                        <span className="font-bold text-green-600">{stats.recipes?.total_approved || 0}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-muted-foreground">En attente</span>
                                                        <span className="font-bold text-orange-600">{stats.recipes?.pending_review || 0}</span>
                                                    </div>
                                                    {stats.recipes?.by_category && Object.keys(stats.recipes.by_category).length > 0 && (
                                                        <div className="pt-2 border-t">
                                                            <p className="text-sm font-medium mb-2">Par catégorie:</p>
                                                            <div className="flex flex-wrap gap-2">
                                                                {Object.entries(stats.recipes.by_category).map(([cat, count]) => (
                                                                    <span key={cat} className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                                                                        {cat}: <strong>{count}</strong>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </CardContent>
                                        </Card>

                                        {/* Deals */}
                                        <Card className="rounded-2xl">
                                            <CardHeader className="pb-2">
                                                <CardTitle className="font-heading flex items-center gap-2 text-lg">
                                                    <Tag className="w-5 h-5 text-red-600" />
                                                    Rabais
                                                </CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="space-y-4">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-muted-foreground">Total</span>
                                                        <span className="font-bold">{stats.deals?.total || 0}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-muted-foreground">Publiés</span>
                                                        <span className="font-bold text-green-600">{stats.deals?.published || 0}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-muted-foreground">Non publiés</span>
                                                        <span className="font-bold text-gray-500">{stats.deals?.unpublished || 0}</span>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>

                                        {/* Packs */}
                                        <Card className="rounded-2xl">
                                            <CardHeader className="pb-2">
                                                <CardTitle className="font-heading flex items-center gap-2 text-lg">
                                                    <Calendar className="w-5 h-5 text-purple-600" />
                                                    Packs Hebdomadaires
                                                </CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="space-y-4">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-muted-foreground">Total</span>
                                                        <span className="font-bold">{stats.packs?.total || 0}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-muted-foreground">Publiés</span>
                                                        <span className="font-bold text-green-600">{stats.packs?.published || 0}</span>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>

                                        {/* Tips & AI */}
                                        <Card className="rounded-2xl">
                                            <CardHeader className="pb-2">
                                                <CardTitle className="font-heading flex items-center gap-2 text-lg">
                                                    <Sparkles className="w-5 h-5 text-amber-600" />
                                                    Astuces & IA
                                                </CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="space-y-4">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-muted-foreground">Astuces approuvées</span>
                                                        <span className="font-bold text-green-600">{stats.tips?.total_approved || 0}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-muted-foreground">Astuces en attente</span>
                                                        <span className="font-bold text-orange-600">{stats.tips?.pending_review || 0}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-muted-foreground">Packs IA générés</span>
                                                        <span className="font-bold text-purple-600">{stats.personal_packs?.total_generated || 0}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-muted-foreground">Favoris totaux</span>
                                                        <span className="font-bold text-pink-600">{stats.favorites?.total || 0}</span>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {/* Traffic Analytics Section */}
                                    <Card className="rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="font-heading flex items-center gap-2 text-lg">
                                                <Activity className="w-5 h-5 text-indigo-600" />
                                                Trafic & Affluence
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            {stats.traffic ? (
                                                <div className="space-y-6">
                                                    {/* Today's overview */}
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                        <div className="p-4 bg-white rounded-xl text-center shadow-sm">
                                                            <p className="text-3xl font-bold text-indigo-700">{stats.traffic.visits_today || 0}</p>
                                                            <p className="text-sm text-indigo-600">Visites aujourd'hui</p>
                                                        </div>
                                                        <div className="p-4 bg-white rounded-xl text-center shadow-sm">
                                                            <p className="text-3xl font-bold text-purple-700">{stats.traffic.unique_visitors_today || 0}</p>
                                                            <p className="text-sm text-purple-600">Visiteurs uniques</p>
                                                        </div>
                                                        <div className="p-4 bg-white rounded-xl text-center shadow-sm">
                                                            <p className="text-3xl font-bold text-blue-700">{stats.traffic.visits_yesterday || 0}</p>
                                                            <p className="text-sm text-blue-600">Hier</p>
                                                        </div>
                                                        <div className="p-4 bg-white rounded-xl text-center shadow-sm">
                                                            <p className="text-3xl font-bold text-green-700">{stats.traffic.total_visits || 0}</p>
                                                            <p className="text-sm text-green-600">Total historique</p>
                                                        </div>
                                                    </div>

                                                    {/* Visits by day chart */}
                                                    {stats.traffic.visits_by_day && stats.traffic.visits_by_day.length > 0 ? (
                                                        <div className="bg-white rounded-xl p-4 shadow-sm">
                                                            <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                                                                <TrendingUp className="w-4 h-4" />
                                                                Visites des 7 derniers jours
                                                            </h4>
                                                            {stats.traffic.visits_by_day.some(d => d.visits > 0) ? (
                                                                <>
                                                                    {/* Bar Chart - Visual Graph */}
                                                                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 mb-4">
                                                                        <div className="flex items-end justify-between gap-3 h-44">
                                                                            {stats.traffic.visits_by_day.slice().reverse().map((day, idx) => {
                                                                                const maxVisits = Math.max(...stats.traffic.visits_by_day.map(d => d.visits), 1);
                                                                                const height = (day.visits / maxVisits) * 100;
                                                                                const [year, month, dayNum] = day.date.split('-').map(Number);
                                                                                const dateObj = new Date(year, month - 1, dayNum);
                                                                                const dayName = dateObj.toLocaleDateString('fr-CA', { weekday: 'short' });
                                                                                const isToday = idx === stats.traffic.visits_by_day.length - 1;
                                                                                return (
                                                                                    <div key={day.date} className="flex flex-col items-center flex-1 group">
                                                                                        <span className={`text-sm font-semibold mb-2 ${isToday ? 'text-indigo-600' : 'text-gray-600'}`}>
                                                                                            {day.visits}
                                                                                        </span>
                                                                                        <div 
                                                                                            className={`w-full rounded-t-xl transition-all duration-300 shadow-sm group-hover:shadow-md ${
                                                                                                isToday 
                                                                                                    ? 'bg-gradient-to-t from-indigo-600 to-indigo-400' 
                                                                                                    : 'bg-gradient-to-t from-purple-500 to-purple-300'
                                                                                            }`}
                                                                                            style={{ height: `${Math.max(height, 8)}%`, minHeight: '12px' }}
                                                                                        />
                                                                                        <span className={`text-xs mt-2 capitalize font-medium ${isToday ? 'text-indigo-600' : 'text-gray-500'}`}>
                                                                                            {dayName}
                                                                                        </span>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    {/* Data Table - Detailed View */}
                                                                    <div className="border-t pt-3">
                                                                        <table className="w-full text-sm">
                                                                            <thead>
                                                                                <tr className="text-gray-500 text-xs">
                                                                                    <th className="text-left py-1 font-medium">Date</th>
                                                                                    <th className="text-center py-1 font-medium">Jour</th>
                                                                                    <th className="text-right py-1 font-medium">Visites</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {stats.traffic.visits_by_day.slice().reverse().map((day, idx) => {
                                                                                    // Parse date as local time (not UTC) to avoid timezone shift
                                                                                    const [year, month, dayNum] = day.date.split('-').map(Number);
                                                                                    const date = new Date(year, month - 1, dayNum);
                                                                                    const dayName = date.toLocaleDateString('fr-CA', { weekday: 'long' });
                                                                                    const formattedDate = date.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' });
                                                                                    const isToday = idx === stats.traffic.visits_by_day.length - 1;
                                                                                    return (
                                                                                        <tr key={day.date} className={`border-t border-gray-100 ${isToday ? 'bg-indigo-50' : ''}`}>
                                                                                            <td className="py-2 text-gray-600">{formattedDate}</td>
                                                                                            <td className="py-2 text-center text-gray-500 capitalize">{dayName}</td>
                                                                                            <td className={`py-2 text-right font-medium ${isToday ? 'text-indigo-600' : 'text-gray-700'}`}>
                                                                                                {day.visits}
                                                                                                {isToday && <span className="ml-1 text-xs text-indigo-400">(aujourd'hui)</span>}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                            <tfoot>
                                                                                <tr className="border-t-2 border-gray-200">
                                                                                    <td colSpan="2" className="py-2 font-medium text-gray-700">Total 7 jours</td>
                                                                                    <td className="py-2 text-right font-bold text-indigo-600">
                                                                                        {stats.traffic.visits_by_day.reduce((sum, d) => sum + d.visits, 0)}
                                                                                    </td>
                                                                                </tr>
                                                                            </tfoot>
                                                                        </table>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <div className="h-32 flex items-center justify-center text-gray-400">
                                                                    <p className="text-sm italic">Aucune visite enregistrée ces 7 derniers jours</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : null}

                                                    <div className="grid md:grid-cols-2 gap-4">
                                                        {/* Peak hours - Today */}
                                                        <div className="bg-white rounded-xl p-4 shadow-sm">
                                                            <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                                                                <Clock className="w-4 h-4" />
                                                                Heures de pointe (aujourd'hui)
                                                            </h4>
                                                            <div className="space-y-2">
                                                                {stats.traffic.peak_hours_today && stats.traffic.peak_hours_today.length > 0 ? (
                                                                    stats.traffic.peak_hours_today.map((hour, idx) => (
                                                                        <div key={hour.hour} className="flex items-center justify-between">
                                                                            <span className="text-sm text-gray-600">
                                                                                {hour.hour}h - {hour.hour + 1}h
                                                                            </span>
                                                                            <span className={`font-bold ${idx === 0 ? 'text-indigo-600' : 'text-gray-600'}`}>
                                                                                {hour.visits} visites
                                                                            </span>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <p className="text-sm text-gray-400 italic">Pas encore de données aujourd'hui</p>
                                                                )}
                                                            </div>
                                                            {stats.traffic.peak_hours && stats.traffic.peak_hours.length > 0 && (
                                                                <div className="mt-4 pt-3 border-t">
                                                                    <p className="text-xs text-gray-500 mb-2">Tendance 7 jours:</p>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {stats.traffic.peak_hours.slice(0, 3).map((hour) => (
                                                                            <span key={hour.hour} className="text-xs px-2 py-1 bg-indigo-50 text-indigo-600 rounded-full">
                                                                                {hour.hour}h: {hour.visits}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Device breakdown */}
                                                        {stats.traffic.device_breakdown && (
                                                            <div className="bg-white rounded-xl p-4 shadow-sm">
                                                                <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                                                                    <Smartphone className="w-4 h-4" />
                                                                    Appareils (7 derniers jours)
                                                                </h4>
                                                                <div className="space-y-3">
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-sm text-gray-600 flex items-center gap-2">
                                                                            <Smartphone className="w-4 h-4 text-blue-500" />
                                                                            Mobile
                                                                        </span>
                                                                        <span className="font-bold text-blue-600">{stats.traffic.device_breakdown.mobile || 0}</span>
                                                                    </div>
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-sm text-gray-600 flex items-center gap-2">
                                                                            <Tablet className="w-4 h-4 text-purple-500" />
                                                                            Tablette
                                                                        </span>
                                                                        <span className="font-bold text-purple-600">{stats.traffic.device_breakdown.tablet || 0}</span>
                                                                    </div>
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-sm text-gray-600 flex items-center gap-2">
                                                                            <Monitor className="w-4 h-4 text-green-500" />
                                                                            Desktop
                                                                        </span>
                                                                        <span className="font-bold text-green-600">{stats.traffic.device_breakdown.desktop || 0}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Popular pages */}
                                                    {stats.traffic.popular_pages && stats.traffic.popular_pages.length > 0 && (
                                                        <div className="bg-white rounded-xl p-4 shadow-sm">
                                                            <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                                                                <Globe className="w-4 h-4" />
                                                                Pages les plus visitées (7 derniers jours)
                                                            </h4>
                                                            <div className="space-y-2">
                                                                {stats.traffic.popular_pages.slice(0, 5).map((page, idx) => (
                                                                    <div key={page.path} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
                                                                        <span className="text-sm text-gray-600 font-mono truncate max-w-[70%]">
                                                                            {page.path}
                                                                        </span>
                                                                        <span className={`font-bold ${idx === 0 ? 'text-indigo-600' : 'text-gray-600'}`}>
                                                                            {page.visits}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-center text-muted-foreground py-4">
                                                    Les statistiques de trafic commenceront à s'afficher après quelques visites.
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>

                                    {/* ============== ENGAGEMENT & CONTENU ============== */}
                                    <Card className="rounded-2xl shadow-lg border-0 bg-gradient-to-br from-emerald-50 to-teal-50">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="font-heading flex items-center gap-2 text-lg text-emerald-800">
                                                <TrendingUp className="w-5 h-5" />
                                                Engagement & Contenu
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="grid md:grid-cols-2 gap-6">
                                                {/* Top 10 recettes consultées */}
                                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                                                        <Eye className="w-4 h-4 text-emerald-600" />
                                                        Top 10 recettes consultées
                                                    </h4>
                                                    {stats.engagement?.top_recipes?.length > 0 ? (
                                                        <div className="space-y-2 max-h-64 overflow-y-auto">
                                                            {stats.engagement.top_recipes.map((recipe, idx) => (
                                                                <div key={recipe.id} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                                                            idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                                                                            idx === 1 ? 'bg-gray-300 text-gray-700' :
                                                                            idx === 2 ? 'bg-orange-300 text-orange-800' :
                                                                            'bg-gray-100 text-gray-500'
                                                                        }`}>{idx + 1}</span>
                                                                        <span className="text-sm text-gray-700 truncate max-w-[180px]">{recipe.title}</span>
                                                                    </div>
                                                                    <span className="font-bold text-emerald-600">{recipe.views}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-400 italic">Aucune donnée disponible</p>
                                                    )}
                                                </div>

                                                {/* Recettes favorites */}
                                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                                                        <Heart className="w-4 h-4 text-red-500" />
                                                        Recettes les plus aimées
                                                    </h4>
                                                    {stats.engagement?.top_favorites?.length > 0 ? (
                                                        <div className="space-y-2 max-h-64 overflow-y-auto">
                                                            {stats.engagement.top_favorites.map((recipe, idx) => (
                                                                <div key={recipe.id} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
                                                                    <span className="text-sm text-gray-700 truncate max-w-[70%]">{recipe.title}</span>
                                                                    <span className="flex items-center gap-1 font-bold text-red-500">
                                                                        <Heart className="w-3 h-3 fill-current" /> {recipe.favorites}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-400 italic">Aucun favori enregistré</p>
                                                    )}
                                                </div>

                                                {/* Termes de recherche populaires */}
                                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                                                        <Search className="w-4 h-4 text-blue-500" />
                                                        Recherches populaires
                                                    </h4>
                                                    {stats.engagement?.popular_searches?.length > 0 ? (
                                                        <div className="flex flex-wrap gap-2">
                                                            {stats.engagement.popular_searches.map((search, idx) => (
                                                                <span key={search.term} className={`px-3 py-1 rounded-full text-sm ${
                                                                    idx === 0 ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-600'
                                                                }`}>
                                                                    {search.term} <span className="text-xs opacity-70">({search.count})</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-400 italic">Aucune recherche enregistrée</p>
                                                    )}
                                                </div>

                                                {/* Catégories populaires - Camembert simple */}
                                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                                                        <Tag className="w-4 h-4 text-purple-500" />
                                                        Catégories populaires
                                                    </h4>
                                                    {stats.engagement?.category_popularity?.length > 0 ? (
                                                        <div className="space-y-2">
                                                            {stats.engagement.category_popularity.slice(0, 6).map((cat, idx) => {
                                                                const total = stats.engagement.category_popularity.reduce((s, c) => s + c.recipes_count, 0);
                                                                const percent = total > 0 ? Math.round(cat.recipes_count / total * 100) : 0;
                                                                const colors = ['bg-purple-500', 'bg-indigo-500', 'bg-blue-500', 'bg-cyan-500', 'bg-teal-500', 'bg-emerald-500'];
                                                                return (
                                                                    <div key={cat.category} className="flex items-center gap-2">
                                                                        <div className={`w-3 h-3 rounded-full ${colors[idx % colors.length]}`}></div>
                                                                        <span className="text-sm text-gray-600 capitalize flex-1">{cat.category}</span>
                                                                        <span className="text-sm font-medium text-gray-700">{cat.recipes_count}</span>
                                                                        <span className="text-xs text-gray-400">({percent}%)</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-400 italic">Aucune catégorie</p>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* ============== CONVERSION & REVENUS ============== */}
                                    <Card className="rounded-2xl shadow-lg border-0 bg-gradient-to-br from-amber-50 to-orange-50">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="font-heading flex items-center gap-2 text-lg text-amber-800">
                                                <DollarSign className="w-5 h-5" />
                                                Conversion & Revenus
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            {/* KPIs principaux */}
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                                <div className="bg-white rounded-xl p-4 shadow-sm text-center">
                                                    <p className="text-3xl font-bold text-amber-600">{stats.conversion?.conversion_rate || 0}%</p>
                                                    <p className="text-sm text-amber-700">Taux de conversion</p>
                                                </div>
                                                <div className="bg-white rounded-xl p-4 shadow-sm text-center">
                                                    <p className="text-3xl font-bold text-green-600">{stats.conversion?.mrr_estimate?.toFixed(2) || '0.00'}$</p>
                                                    <p className="text-sm text-green-700">MRR estimé</p>
                                                </div>
                                                <div className="bg-white rounded-xl p-4 shadow-sm text-center">
                                                    <p className="text-3xl font-bold text-blue-600">+{stats.conversion?.new_premium_this_month || 0}</p>
                                                    <p className="text-sm text-blue-700">Nouveaux premium (mois)</p>
                                                </div>
                                                <div className="bg-white rounded-xl p-4 shadow-sm text-center">
                                                    <p className="text-3xl font-bold text-red-500">{stats.conversion?.churn_rate || 0}%</p>
                                                    <p className="text-sm text-red-600">Taux de churn</p>
                                                </div>
                                            </div>

                                            {/* Détails abonnements */}
                                            <div className="grid md:grid-cols-2 gap-4">
                                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                                    <h4 className="font-medium text-gray-700 mb-3">Répartition des abonnés</h4>
                                                    <div className="space-y-2">
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-sm text-gray-600">Abonnés hebdo (5.99$/sem)</span>
                                                            <span className="font-bold text-amber-600">{stats.conversion?.weekly_subscribers || 0}</span>
                                                        </div>
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-sm text-gray-600">Abonnés mensuels (19.99$/mois)</span>
                                                            <span className="font-bold text-amber-600">{stats.conversion?.monthly_subscribers || 0}</span>
                                                        </div>
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-sm text-gray-600">Premium offerts (cadeaux)</span>
                                                            <span className="font-bold text-purple-600">{stats.conversion?.gift_subscribers || 0}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                                    <h4 className="font-medium text-gray-700 mb-3">Funnel de conversion</h4>
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex-1 bg-gray-200 rounded-full h-4">
                                                                <div className="bg-blue-500 rounded-full h-4" style={{width: '100%'}}></div>
                                                            </div>
                                                            <span className="text-sm w-20">Inscrits: {stats.users?.total || 0}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex-1 bg-gray-200 rounded-full h-4">
                                                                <div className="bg-green-500 rounded-full h-4" style={{width: `${stats.conversion?.conversion_rate || 0}%`}}></div>
                                                            </div>
                                                            <span className="text-sm w-20">Premium: {stats.users?.premium || 0}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* ============== UTILISATEURS ACTIFS ============== */}
                                    <Card className="rounded-2xl shadow-lg border-0 bg-gradient-to-br from-sky-50 to-blue-50">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="font-heading flex items-center gap-2 text-lg text-sky-800">
                                                <Users className="w-5 h-5" />
                                                Activité des utilisateurs
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            {/* KPIs activité */}
                                            <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-6">
                                                <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                                                    <p className="text-2xl font-bold text-sky-600">{stats.user_activity?.daily_active_users || 0}</p>
                                                    <p className="text-xs text-sky-700">DAU</p>
                                                </div>
                                                <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                                                    <p className="text-2xl font-bold text-sky-600">{stats.user_activity?.weekly_active_users || 0}</p>
                                                    <p className="text-xs text-sky-700">WAU</p>
                                                </div>
                                                <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                                                    <p className="text-2xl font-bold text-sky-600">{stats.user_activity?.monthly_active_users || 0}</p>
                                                    <p className="text-xs text-sky-700">MAU</p>
                                                </div>
                                                <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                                                    <p className="text-2xl font-bold text-emerald-600">{stats.user_activity?.retention_7_days || 0}%</p>
                                                    <p className="text-xs text-emerald-700">Rétention 7j</p>
                                                </div>
                                                <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                                                    <p className="text-2xl font-bold text-emerald-600">{stats.user_activity?.retention_30_days || 0}%</p>
                                                    <p className="text-xs text-emerald-700">Rétention 30j</p>
                                                </div>
                                                <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                                                    <p className="text-2xl font-bold text-purple-600">{stats.users?.total || 0}</p>
                                                    <p className="text-xs text-purple-700">Total inscrits</p>
                                                </div>
                                            </div>

                                            {/* Graphique nouveaux inscrits */}
                                            {stats.user_activity?.new_users_by_day && (
                                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                                                        <UserPlus className="w-4 h-4 text-sky-500" />
                                                        Nouveaux inscrits (7 derniers jours)
                                                    </h4>
                                                    <div className="flex items-end justify-between gap-3 h-32">
                                                        {stats.user_activity.new_users_by_day.slice().reverse().map((day, idx) => {
                                                            const maxCount = Math.max(...stats.user_activity.new_users_by_day.map(d => d.count), 1);
                                                            const height = (day.count / maxCount) * 100;
                                                            const [year, month, dayNum] = day.date.split('-').map(Number);
                                                            const dateObj = new Date(year, month - 1, dayNum);
                                                            const dayName = dateObj.toLocaleDateString('fr-CA', { weekday: 'short' });
                                                            const isToday = idx === stats.user_activity.new_users_by_day.length - 1;
                                                            return (
                                                                <div key={day.date} className="flex flex-col items-center flex-1">
                                                                    <span className={`text-sm font-semibold mb-1 ${isToday ? 'text-sky-600' : 'text-gray-600'}`}>
                                                                        {day.count}
                                                                    </span>
                                                                    <div 
                                                                        className={`w-full rounded-t-lg transition-all ${
                                                                            isToday ? 'bg-gradient-to-t from-sky-500 to-sky-300' : 'bg-gradient-to-t from-blue-400 to-blue-200'
                                                                        }`}
                                                                        style={{ height: `${Math.max(height, 8)}%`, minHeight: '8px' }}
                                                                    />
                                                                    <span className={`text-xs mt-1 capitalize ${isToday ? 'text-sky-600 font-medium' : 'text-gray-500'}`}>
                                                                        {dayName}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>

                                    {/* Configuration Section */}
                                    <Card className="rounded-2xl border-2 border-dashed">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="font-heading flex items-center gap-2 text-lg">
                                                <Settings className="w-5 h-5 text-gray-600" />
                                                Configuration système
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="grid md:grid-cols-2 gap-4">
                                                <div className="p-4 bg-gray-50 rounded-xl">
                                                    <p className="text-sm text-muted-foreground mb-1">URL Frontend (emails)</p>
                                                    <p className="font-mono text-sm break-all">{stats.config?.frontend_url || 'Non configuré'}</p>
                                                    {stats.config?.frontend_url && !stats.config.frontend_url.includes('madame-cameleon') && (
                                                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                                            <AlertCircle className="w-3 h-3" />
                                                            Attention: URL de preview détectée
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="p-4 bg-gray-50 rounded-xl">
                                                    <p className="text-sm text-muted-foreground mb-1">Service Email (Resend)</p>
                                                    <p className={`font-medium ${stats.config?.email_service_configured ? 'text-green-600' : 'text-red-600'}`}>
                                                        {stats.config?.email_service_configured ? '✓ Configuré' : '✗ Non configuré'}
                                                    </p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* PACKS TAB */}
                    <TabsContent value="packs">
                        <Card className="rounded-2xl">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Calendar className="w-5 h-5" />
                                    Packs Hebdomadaires
                                </CardTitle>
                                <Button
                                    onClick={() => setShowNewPackForm(true)}
                                    className="bg-primary text-white hover:bg-primary/90 rounded-full"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Nouveau Pack
                                </Button>
                            </CardHeader>
                            <CardContent>
                                {showNewPackForm && (
                                    <PackForm onSave={handleSavePack} onCancel={() => setShowNewPackForm(false)} onRefresh={loadAdminData} availableRecipes={recipes.filter(r => r.status === 'approved')} availableDeals={deals} />
                                )}
                                {editingPack && (
                                    <PackForm 
                                        pack={editingPack} 
                                        onSave={handleSavePack} 
                                        onCancel={() => setEditingPack(null)} 
                                        onRefresh={loadAdminData}
                                        availableRecipes={recipes.filter(r => r.status === 'approved')}
                                        availableDeals={deals}
                                        onGenerateRecommendations={handleGenerateRecommendations}
                                        generatingRecommendations={generatingRecommendations}
                                    />
                                )}
                                {duplicatingPack && (
                                    <PackForm pack={duplicatingPack} onSave={handleSavePack} onCancel={() => setDuplicatingPack(null)} onRefresh={loadAdminData} isDuplicate={true} availableRecipes={recipes.filter(r => r.status === 'approved')} availableDeals={deals} />
                                )}
                                
                                {!showNewPackForm && !editingPack && !duplicatingPack && (
                                    <div className="space-y-4">
                                        {packs.length === 0 ? (
                                            <p className="text-center text-muted-foreground py-8">Aucun pack créé.</p>
                                        ) : (
                                            packs.map((pack) => (
                                                <div key={pack.id} className={`flex items-center justify-between p-4 rounded-xl ${pack.is_published ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                            <Badge variant="secondary">Pack #{pack.pack_number || pack.week_number}</Badge>
                                                            <span className="font-semibold">{pack.title}</span>
                                                            {pack.is_published ? (
                                                                <Badge className="bg-green-500 text-white">✓ Publié</Badge>
                                                            ) : (
                                                                <Badge className="bg-orange-500 text-white">Brouillon</Badge>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-muted-foreground">
                                                            {pack.grocery_list?.length || 0} articles • {pack.deals?.length || 0} rabais • {pack.soupers?.length || 0} soupers
                                                        </p>
                                                        {pack.total_savings > 0 && (
                                                            <p className="text-sm text-green-600 font-semibold">
                                                                💰 Économies: {pack.total_savings?.toFixed(2)}$
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex gap-2 flex-wrap justify-end">
                                                        <Button 
                                                            variant={pack.is_published ? "outline" : "default"}
                                                            size="sm" 
                                                            onClick={() => handleTogglePublish(pack.id, pack.is_published)}
                                                            disabled={publishingPack === pack.id}
                                                            className={pack.is_published ? "text-orange-600 hover:bg-orange-50 border-orange-300" : "bg-green-600 hover:bg-green-700 text-white"}
                                                            title={pack.is_published ? "Dépublier" : "Publier"}
                                                        >
                                                            {publishingPack === pack.id ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : pack.is_published ? (
                                                                <>
                                                                    <EyeOff className="w-4 h-4 mr-1" />
                                                                    Dépublier
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Eye className="w-4 h-4 mr-1" />
                                                                    Publier
                                                                </>
                                                            )}
                                                        </Button>
                                                        {/* Grocery List Generation with Dropdown */}
                                                        <div className="relative group">
                                                            <Button variant="outline" size="sm" onClick={() => handleGenerateGroceryList(pack.id, false)} title="Générer la liste d'épicerie depuis les recettes" className="text-green-600 hover:bg-green-50 rounded-r-none border-r-0">
                                                                <ListChecks className="w-4 h-4" />
                                                            </Button>
                                                            <div className="absolute right-0 top-0 h-full">
                                                                <Button 
                                                                    variant="outline" 
                                                                    size="sm" 
                                                                    className="h-full px-1 text-green-600 hover:bg-green-50 rounded-l-none border-l-0"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const menu = e.currentTarget.nextElementSibling;
                                                                        menu.classList.toggle('hidden');
                                                                    }}
                                                                >
                                                                    <ChevronDown className="w-3 h-3" />
                                                                </Button>
                                                                <div className="hidden absolute right-0 top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg z-50 min-w-[220px]">
                                                                    <button 
                                                                        onClick={() => handleGenerateGroceryList(pack.id, false)}
                                                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm flex items-center gap-2"
                                                                    >
                                                                        <ListChecks className="w-4 h-4 text-green-600" />
                                                                        <span>Rabais publiés seulement</span>
                                                                    </button>
                                                                    <button 
                                                                        onClick={() => handleGenerateGroceryList(pack.id, true)}
                                                                        className="w-full px-3 py-2 text-left hover:bg-orange-50 text-sm flex items-center gap-2 border-t"
                                                                    >
                                                                        <Eye className="w-4 h-4 text-orange-600" />
                                                                        <span className="text-orange-700 font-medium">+ Rabais non publiés (admin)</span>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {pack.grocery_list?.length > 0 && (
                                                            <>
                                                                <Button 
                                                                    variant="outline" 
                                                                    size="sm" 
                                                                    onClick={() => handleRefreshDeals(pack.id)} 
                                                                    title="Rafraîchir les correspondances de rabais"
                                                                    className="text-blue-600 hover:bg-blue-50"
                                                                >
                                                                    <TrendingUp className="w-4 h-4" />
                                                                </Button>
                                                                <Button variant="outline" size="sm" onClick={() => handleOpenGroceryEditor(pack.id)} title="Modifier les correspondances rabais" className="text-orange-600 hover:bg-orange-50">
                                                                <ShoppingCart className="w-4 h-4" />
                                                            </Button>
                                                            </>
                                                        )}
                                                        <Button variant="outline" size="sm" onClick={() => handleSendPackNotification(pack)} title="Envoyer notification email" className="text-purple-600 hover:bg-purple-50">
                                                            <Bell className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="outline" size="sm" onClick={() => handleDuplicatePack(pack)} title="Dupliquer" className="text-blue-600">
                                                            <Copy className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="outline" size="sm" onClick={() => setEditingPack(pack)} title="Modifier">
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="outline" size="sm" onClick={() => handleDeletePack(pack.id)} className="text-destructive" title="Supprimer">
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* RECIPES TAB */}
                    <TabsContent value="recipes">
                        <Card className="rounded-2xl">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <ChefHat className="w-5 h-5" />
                                    Gestion des Recettes
                                </CardTitle>
                                <div className="flex gap-2 flex-wrap">
                                    <Button onClick={() => setShowImportRecipeModal(true)} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 rounded-full">
                                        <Link2 className="w-4 h-4 mr-2" />
                                        Importer URL
                                    </Button>
                                    <Button onClick={() => setShowAIRecipeGenerator(true)} className="bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700 rounded-full">
                                        <Wand2 className="w-4 h-4 mr-2" />
                                        Générer avec IA
                                    </Button>
                                    <Button onClick={() => setShowNewRecipeForm(true)} className="bg-primary text-white hover:bg-primary/90 rounded-full">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Nouvelle Recette
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {/* Import Recipe from URL Modal */}
                                {showImportRecipeModal && (
                                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                                        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-fade-in">
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="font-heading font-bold text-xl flex items-center gap-2">
                                                    <span className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                                                        <Link2 className="w-5 h-5 text-white" />
                                                    </span>
                                                    Importer une recette
                                                </h3>
                                                <Button variant="ghost" size="icon" onClick={() => setShowImportRecipeModal(false)}>
                                                    <X className="w-5 h-5" />
                                                </Button>
                                            </div>
                                            
                                            <p className="text-muted-foreground mb-4">
                                                Collez l'URL d'une recette depuis n'importe quel site (Ricardo, Trois fois par jour, Pinterest, etc.). 
                                                L'IA extraira automatiquement les informations.
                                            </p>
                                            
                                            <div className="space-y-4">
                                                <div>
                                                    <Label>URL de la recette</Label>
                                                    <Input
                                                        type="url"
                                                        value={importUrl}
                                                        onChange={(e) => setImportUrl(e.target.value)}
                                                        placeholder="https://www.ricardocuisine.com/recettes/..."
                                                        className="mt-1"
                                                        disabled={importingRecipe}
                                                    />
                                                </div>
                                                
                                                <div className="bg-blue-50 rounded-xl p-3 text-sm">
                                                    <p className="font-medium text-blue-800 mb-1">💡 L'import inclut:</p>
                                                    <ul className="text-blue-700 space-y-1">
                                                        <li>• Extraction automatique du titre, ingrédients, étapes</li>
                                                        <li>• Calcul des coûts estimés</li>
                                                        <li>• Attribution de la source originale</li>
                                                        <li>• Mode brouillon pour révision</li>
                                                    </ul>
                                                </div>
                                                
                                                <div className="flex gap-3 justify-end">
                                                    <Button variant="outline" onClick={() => setShowImportRecipeModal(false)} disabled={importingRecipe}>
                                                        Annuler
                                                    </Button>
                                                    <Button 
                                                        onClick={handleImportRecipe} 
                                                        disabled={importingRecipe || !importUrl.trim()}
                                                        className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                                                    >
                                                        {importingRecipe ? (
                                                            <>
                                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                Import en cours...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Download className="w-4 h-4 mr-2" />
                                                                Importer
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {/* AI Recipe Generator Modal */}
                                {showAIRecipeGenerator && (
                                    <AIGeneratorModal
                                        type="recipe"
                                        items={aiGeneratedRecipes}
                                        loading={generatingRecipes}
                                        onGenerate={handleGenerateRecipes}
                                        onSelect={handleSelectAIRecipe}
                                        onClose={() => { setShowAIRecipeGenerator(false); setAiError(null); }}
                                        error={aiError}
                                    />
                                )}
                                
                                {/* Selected AI Recipe Form */}
                                {selectedAIRecipe && (
                                    <RecipeForm 
                                        recipe={selectedAIRecipe} 
                                        onSave={(data) => {
                                            handleSaveRecipe(data);
                                            setSelectedAIRecipe(null);
                                        }} 
                                        onCancel={() => setSelectedAIRecipe(null)} 
                                        availableRecipes={recipes.filter(r => r.status === 'approved')} 
                                    />
                                )}
                                
                                {showNewRecipeForm && (
                                    <RecipeForm onSave={handleSaveRecipe} onCancel={() => setShowNewRecipeForm(false)} availableRecipes={recipes.filter(r => r.status === 'approved')} />
                                )}
                                {editingRecipe && (
                                    <RecipeForm recipe={editingRecipe} onSave={handleSaveRecipe} onCancel={() => setEditingRecipe(null)} availableRecipes={recipes.filter(r => r.status === 'approved' && r.id !== editingRecipe.id)} />
                                )}
                                
                                {!showNewRecipeForm && !editingRecipe && !selectedAIRecipe && !showAIRecipeGenerator && (
                                    <div className="space-y-4">
                                        {/* Search bar for recipes */}
                                        <div className="relative mb-4">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <Input
                                                type="text"
                                                placeholder="Rechercher une recette..."
                                                value={adminRecipeSearch}
                                                onChange={(e) => setAdminRecipeSearch(e.target.value)}
                                                className="pl-10 pr-10"
                                                data-testid="admin-recipe-search"
                                            />
                                            {adminRecipeSearch && (
                                                <button
                                                    onClick={() => setAdminRecipeSearch('')}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                        
                                        {/* Pending recipes first */}
                                        {pendingRecipes.length > 0 && !adminRecipeSearch && (
                                            <div className="mb-6">
                                                <h3 className="font-semibold text-orange-600 mb-3 flex items-center gap-2">
                                                    <AlertCircle className="w-4 h-4" />
                                                    En attente de modération ({pendingRecipes.length})
                                                </h3>
                                                {pendingRecipes.map((recipe) => (
                                                    <RecipeItem
                                                        key={recipe.id}
                                                        recipe={recipe}
                                                        onEdit={() => setEditingRecipe(recipe)}
                                                        onDelete={() => handleDeleteRecipe(recipe.id)}
                                                        onApprove={() => handleApproveRecipe(recipe.id)}
                                                        onReject={() => handleRejectRecipe(recipe.id)}
                                                        showModeration={true}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* Approved recipes */}
                                        <h3 className="font-semibold text-green-600 mb-3 flex items-center gap-2">
                                            <Check className="w-4 h-4" />
                                            Recettes approuvées ({recipes.filter(r => r.status === 'approved').length})
                                            {adminRecipeSearch && (
                                                <span className="text-sm font-normal text-gray-500">
                                                    — {recipes.filter(r => r.status === 'approved' && r.title.toLowerCase().includes(adminRecipeSearch.toLowerCase())).length} résultat(s)
                                                </span>
                                            )}
                                        </h3>
                                        {recipes
                                            .filter(r => r.status === 'approved')
                                            .filter(r => !adminRecipeSearch || r.title.toLowerCase().includes(adminRecipeSearch.toLowerCase()))
                                            .map((recipe) => (
                                            <RecipeItem
                                                key={recipe.id}
                                                recipe={recipe}
                                                onEdit={() => setEditingRecipe(recipe)}
                                                onDelete={() => handleDeleteRecipe(recipe.id)}
                                            />
                                        ))}
                                        {adminRecipeSearch && recipes.filter(r => r.status === 'approved' && r.title.toLowerCase().includes(adminRecipeSearch.toLowerCase())).length === 0 && (
                                            <p className="text-gray-500 text-center py-4">Aucune recette trouvée pour "{adminRecipeSearch}"</p>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* TIPS TAB */}
                    <TabsContent value="tips">
                        <Card className="rounded-2xl">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Lightbulb className="w-5 h-5" />
                                    Gestion des Astuces
                                </CardTitle>
                                <div className="flex gap-2">
                                    <Button onClick={() => setShowAITipGenerator(true)} className="bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700 rounded-full">
                                        <Wand2 className="w-4 h-4 mr-2" />
                                        Générer avec IA
                                    </Button>
                                    <Button onClick={() => setShowNewTipForm(true)} className="bg-primary text-white hover:bg-primary/90 rounded-full">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Nouvelle Astuce
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {/* AI Tip Generator Modal */}
                                {showAITipGenerator && (
                                    <AIGeneratorModal
                                        type="tip"
                                        items={aiGeneratedTips}
                                        loading={generatingTips}
                                        onGenerate={handleGenerateTips}
                                        onSelect={handleSelectAITip}
                                        onClose={() => { setShowAITipGenerator(false); setAiError(null); }}
                                        error={aiError}
                                    />
                                )}
                                
                                {/* Selected AI Tip Form */}
                                {selectedAITip && (
                                    <TipForm 
                                        tip={selectedAITip} 
                                        onSave={(data) => {
                                            handleSaveTip(data);
                                            setSelectedAITip(null);
                                        }} 
                                        onCancel={() => setSelectedAITip(null)} 
                                    />
                                )}
                                
                                {showNewTipForm && (
                                    <TipForm onSave={handleSaveTip} onCancel={() => setShowNewTipForm(false)} />
                                )}
                                {editingTip && (
                                    <TipForm tip={editingTip} onSave={handleSaveTip} onCancel={() => setEditingTip(null)} />
                                )}
                                
                                {!showNewTipForm && !editingTip && !selectedAITip && !showAITipGenerator && (
                                    <div className="space-y-4">
                                        {/* Pending tips first */}
                                        {pendingTips.length > 0 && (
                                            <div className="mb-6">
                                                <h3 className="font-semibold text-orange-600 mb-3 flex items-center gap-2">
                                                    <AlertCircle className="w-4 h-4" />
                                                    En attente de modération ({pendingTips.length})
                                                </h3>
                                                {pendingTips.map((tip) => (
                                                    <TipItem
                                                        key={tip.id}
                                                        tip={tip}
                                                        onEdit={() => setEditingTip(tip)}
                                                        onDelete={() => handleDeleteTip(tip.id)}
                                                        onApprove={() => handleApproveTip(tip.id)}
                                                        onReject={() => handleRejectTip(tip.id)}
                                                        showModeration={true}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* Approved tips */}
                                        <h3 className="font-semibold text-green-600 mb-3 flex items-center gap-2">
                                            <Check className="w-4 h-4" />
                                            Astuces approuvées ({tips.filter(t => t.status === 'approved').length})
                                        </h3>
                                        {tips.filter(t => t.status === 'approved').map((tip) => (
                                            <TipItem
                                                key={tip.id}
                                                tip={tip}
                                                onEdit={() => setEditingTip(tip)}
                                                onDelete={() => handleDeleteTip(tip.id)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* DEALS TAB */}
                    <TabsContent value="deals">
                        <Card className="rounded-2xl">
                            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Tag className="w-5 h-5" />
                                    Gestion des Rabais
                                </CardTitle>
                                <div className="flex gap-2 flex-wrap">
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => setShowAIExtractForm(true)}
                                        className="text-purple-600 border-purple-300 hover:bg-purple-50"
                                    >
                                        <Sparkles className="w-4 h-4 mr-1" />
                                        Extraire par IA
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handlePublishAllDeals(true)}
                                        disabled={publishingAllDeals}
                                        className="text-green-600 border-green-300 hover:bg-green-50"
                                    >
                                        {publishingAllDeals ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                                        Tout publier
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handlePublishAllDeals(false)}
                                        disabled={publishingAllDeals}
                                        className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                    >
                                        {publishingAllDeals ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}
                                        Tout dépublier
                                    </Button>
                                    <Button onClick={() => setShowNewDealForm(true)} className="bg-primary text-white hover:bg-primary/90 rounded-full">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Nouveau Rabais
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {/* AI Extraction Form */}
                                {showAIExtractForm && (
                                    <div className="border-2 border-purple-200 rounded-xl p-6 mb-6 bg-gradient-to-br from-purple-50 to-white">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <Sparkles className="w-5 h-5 text-purple-600" />
                                                <h3 className="font-bold text-lg text-purple-800">Extraction par IA</h3>
                                            </div>
                                            <Button variant="ghost" size="sm" onClick={() => { setShowAIExtractForm(false); setExtractedDeals([]); }}>
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                        
                                        <p className="text-sm text-purple-600 mb-2">
                                            Uploadez une capture d'écran de circulaire (Reebee, site web, etc.) et l'IA extraira automatiquement les rabais.
                                        </p>
                                        <p className="text-xs text-purple-400 mb-4">
                                            💡 Conseil: Les captures d'écran sont plus rapides que les gros PDFs de circulaires.
                                        </p>
                                        
                                        {extractedDeals.length === 0 ? (
                                            <div className="space-y-4">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <Label className="text-purple-700">Magasin (optionnel)</Label>
                                                        <select 
                                                            id="store-hint-select"
                                                            className="w-full border border-purple-200 rounded-lg p-2 mt-1"
                                                            defaultValue=""
                                                        >
                                                            <option value="">-- Détection automatique --</option>
                                                            <option value="Maxi">Maxi</option>
                                                            <option value="Métro">Métro</option>
                                                            <option value="IGA">IGA</option>
                                                            <option value="Super C">Super C</option>
                                                            <option value="Tigre Géant">Tigre Géant</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <Label className="text-purple-700">Pages à analyser (PDF)</Label>
                                                        <select 
                                                            id="pdf-pages-select"
                                                            className="w-full border border-purple-200 rounded-lg p-2 mt-1"
                                                            defaultValue="3"
                                                        >
                                                            <option value="1">1 page (plus rapide)</option>
                                                            <option value="2">2 pages</option>
                                                            <option value="3">3 pages</option>
                                                            <option value="5">5 pages</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                
                                                <div 
                                                    className="border-2 border-dashed border-purple-300 rounded-xl p-8 text-center bg-white hover:bg-purple-50 transition-colors relative"
                                                >
                                                    <input
                                                        type="file"
                                                        accept="image/*,.pdf,application/pdf"
                                                        id="deal-image-upload"
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                        onChange={async (e) => {
                                                            console.log('File input change triggered');
                                                            const file = e.target.files?.[0];
                                                            console.log('Selected file:', file?.name, file?.type, file?.size);
                                                            if (file) {
                                                                const storeHint = document.getElementById('store-hint-select')?.value || '';
                                                                const maxPages = parseInt(document.getElementById('pdf-pages-select')?.value || '3');
                                                                console.log('Store hint:', storeHint, 'Max pages:', maxPages);
                                                                
                                                                // Check if PDF
                                                                const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
                                                                console.log('Is PDF:', isPdf, 'Type:', file.type);
                                                                
                                                                if (isPdf) {
                                                                    console.log('Calling handleExtractDealsFromPdf...');
                                                                    await handleExtractDealsFromPdf(file, storeHint, maxPages);
                                                                    console.log('handleExtractDealsFromPdf completed');
                                                                    return;
                                                                }
                                                                
                                                                // Handle image
                                                                console.log('Processing as image...');
                                                                const reader = new FileReader();
                                                                reader.onload = async (event) => {
                                                                    const base64 = event.target.result;
                                                                    await handleExtractDealsFromImage(base64, storeHint);
                                                                };
                                                                reader.readAsDataURL(file);
                                                            }
                                                            // Reset input value to allow selecting the same file again
                                                            e.target.value = '';
                                                        }}
                                                        disabled={extractingDeals}
                                                    />
                                                    <div className="pointer-events-none">
                                                        {extractingDeals ? (
                                                            <div className="flex flex-col items-center gap-2">
                                                                <Loader2 className="w-12 h-12 text-purple-500 animate-spin" />
                                                                <span className="text-purple-600 font-medium">Analyse en cours...</span>
                                                                <span className="text-sm text-purple-500">L'IA extrait les rabais de votre image</span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-col items-center gap-2">
                                                                <Upload className="w-12 h-12 text-purple-400" />
                                                                <span className="text-purple-600 font-medium">Cliquez ou collez une image (Ctrl+V)</span>
                                                                <span className="text-sm text-purple-500">PNG, JPG, capture d'écran ou PDF</span>
                                                                <span className="text-xs text-purple-400 mt-2 border border-purple-200 px-3 py-1 rounded-full">
                                                                    Tip: Copiez une image et collez-la ici directement !
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium text-purple-800">{extractedDeals.length} rabais extraits</span>
                                                    <div className="flex gap-2">
                                                        <Button 
                                                            variant="outline" 
                                                            size="sm"
                                                            onClick={() => setExtractedDeals([])}
                                                            className="text-gray-600"
                                                        >
                                                            Nouvelle image
                                                        </Button>
                                                        <Button 
                                                            onClick={handleSaveExtractedDeals}
                                                            className="bg-green-600 text-white hover:bg-green-700"
                                                        >
                                                            <Check className="w-4 h-4 mr-1" />
                                                            Sauvegarder tout
                                                        </Button>
                                                    </div>
                                                </div>
                                                
                                                <div className="max-h-96 overflow-y-auto space-y-2">
                                                    {extractedDeals.map((deal, index) => (
                                                        <div key={index} className="border border-purple-200 rounded-lg p-3 bg-white flex items-center gap-3">
                                                            <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                                                                <Input
                                                                    value={deal.product_name}
                                                                    onChange={(e) => handleUpdateExtractedDeal(index, 'product_name', e.target.value)}
                                                                    placeholder="Produit"
                                                                    className="text-sm"
                                                                />
                                                                <select
                                                                    value={deal.store_name}
                                                                    onChange={(e) => handleUpdateExtractedDeal(index, 'store_name', e.target.value)}
                                                                    className="border rounded p-2 text-sm"
                                                                >
                                                                    <option value="Maxi">Maxi</option>
                                                                    <option value="Métro">Métro</option>
                                                                    <option value="IGA">IGA</option>
                                                                    <option value="Super C">Super C</option>
                                                                    <option value="Tigre Géant">Tigre Géant</option>
                                                                </select>
                                                                <Input
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={deal.sale_price}
                                                                    onChange={(e) => handleUpdateExtractedDeal(index, 'sale_price', parseFloat(e.target.value) || 0)}
                                                                    placeholder="Prix"
                                                                    className="text-sm"
                                                                />
                                                                <div className="flex items-center gap-1">
                                                                    <span className="text-green-600 font-medium">-{deal.discount_percent}%</span>
                                                                </div>
                                                            </div>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleRemoveExtractedDeal(index)}
                                                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {showNewDealForm && (
                                    <DealForm onSave={handleSaveDeal} onCancel={() => setShowNewDealForm(false)} />
                                )}
                                {editingDeal && (
                                    <DealForm deal={editingDeal} onSave={handleSaveDeal} onCancel={() => setEditingDeal(null)} />
                                )}
                                
                                {!showNewDealForm && !editingDeal && !showAIExtractForm && (
                                    <div className="space-y-4">
                                        {/* Stats */}
                                        <div className="flex gap-4 mb-4 text-sm">
                                            <span className="text-green-600 font-medium">
                                                ✓ {deals.filter(d => d.is_published !== false).length} publiés
                                            </span>
                                            <span className="text-orange-600 font-medium">
                                                ○ {deals.filter(d => d.is_published === false).length} brouillons
                                            </span>
                                        </div>
                                        {deals.length === 0 ? (
                                            <p className="text-center text-muted-foreground py-8">Aucun rabais.</p>
                                        ) : (
                                            deals.map((deal) => (
                                                <DealItem
                                                    key={deal.id}
                                                    deal={deal}
                                                    onEdit={() => setEditingDeal(deal)}
                                                    onDelete={() => handleDeleteDeal(deal.id)}
                                                    onTogglePublish={() => handleToggleDealPublish(deal.id, deal.is_published !== false)}
                                                    isToggling={togglingDealPublish === deal.id}
                                                />
                                            ))
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* DOSSIERS TAB */}
                    <TabsContent value="dossiers">
                        <Card className="rounded-2xl">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <BookOpen className="w-5 h-5" />
                                    Dossiers Spéciaux
                                    <Badge variant="secondary" className="bg-purple-100 text-purple-700">Premium</Badge>
                                </CardTitle>
                                <Button
                                    onClick={() => setShowNewDossierForm(true)}
                                    className="bg-purple-600 text-white hover:bg-purple-700 rounded-full"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Nouveau dossier
                                </Button>
                            </CardHeader>
                            <CardContent>
                                {showNewDossierForm && (
                                    <DossierForm
                                        recipes={recipes.filter(r => r.status === 'approved')}
                                        onSave={handleSaveDossier}
                                        onCancel={() => setShowNewDossierForm(false)}
                                    />
                                )}
                                {editingDossier && (
                                    <DossierForm
                                        dossier={editingDossier}
                                        recipes={recipes.filter(r => r.status === 'approved')}
                                        onSave={handleSaveDossier}
                                        onCancel={() => setEditingDossier(null)}
                                    />
                                )}
                                {!showNewDossierForm && !editingDossier && (
                                    <div className="space-y-2">
                                        {dossiers.length === 0 ? (
                                            <p className="text-center py-8 text-muted-foreground">Aucun dossier</p>
                                        ) : (
                                            dossiers.map(dossier => (
                                                <DossierItem
                                                    key={dossier.id}
                                                    dossier={dossier}
                                                    onEdit={() => setEditingDossier(dossier)}
                                                    onDelete={() => handleDeleteDossier(dossier.id)}
                                                />
                                            ))
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                    
                    {/* USERS TAB */}
                    <TabsContent value="users">
                        <Card className="rounded-2xl">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Users className="w-5 h-5" />
                                    Gestion des Utilisateurs
                                </CardTitle>
                                <Badge variant="outline" className="text-blue-600 border-blue-300">
                                    {users.length} utilisateurs
                                </Badge>
                            </CardHeader>
                            <CardContent>
                                {/* Premium Duration Modal */}
                                {showPremiumModal && (
                                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                                        <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
                                            <h3 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                                                <Crown className="w-5 h-5 text-amber-500" />
                                                Offrir Premium
                                            </h3>
                                            <p className="text-sm text-gray-600 mb-4">
                                                {"Choisissez la durée de l'abonnement gratuit pour"}{' '}
                                                <strong>{users.find(u => u.id === showPremiumModal)?.name}</strong>
                                            </p>
                                            
                                            <div className="grid grid-cols-2 gap-2 mb-4">
                                                {[
                                                    { days: 7, label: '1 semaine' },
                                                    { days: 30, label: '1 mois' },
                                                    { days: 90, label: '3 mois' },
                                                    { days: 180, label: '6 mois' },
                                                    { days: 365, label: '1 an' },
                                                    { days: null, label: 'Illimité' }
                                                ].map((option) => (
                                                    <Button
                                                        key={option.days || 'unlimited'}
                                                        variant={selectedDuration === option.days ? 'default' : 'outline'}
                                                        size="sm"
                                                        onClick={() => setSelectedDuration(option.days)}
                                                        className={selectedDuration === option.days ? 'bg-amber-500 hover:bg-amber-600' : ''}
                                                    >
                                                        {option.label}
                                                    </Button>
                                                ))}
                                            </div>
                                            
                                            <div className="flex gap-2">
                                                <Button
                                                    variant="outline"
                                                    className="flex-1"
                                                    onClick={() => { setShowPremiumModal(null); setSelectedDuration(null); }}
                                                >
                                                    Annuler
                                                </Button>
                                                <Button
                                                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                                                    onClick={() => handleGivePremium(showPremiumModal, selectedDuration)}
                                                    disabled={togglingPremium === showPremiumModal}
                                                >
                                                    {togglingPremium === showPremiumModal ? (
                                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                    ) : (
                                                        <Crown className="w-4 h-4 mr-2" />
                                                    )}
                                                    Confirmer
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="space-y-3">
                                    {users.map((u) => (
                                        <div key={u.id} className={`flex items-center justify-between p-4 rounded-xl ${u.subscription_tier === 'premium' ? (u.premium_expired ? 'bg-gradient-to-r from-red-50 to-orange-50 border border-red-200' : 'bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200') : 'bg-muted/50'}`}>
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${u.subscription_tier === 'premium' ? (u.premium_expired ? 'bg-red-200' : 'bg-amber-200') : 'bg-gray-200'}`}>
                                                    {u.subscription_tier === 'premium' ? (
                                                        <Crown className={`w-5 h-5 ${u.premium_expired ? 'text-red-600' : 'text-amber-600'}`} />
                                                    ) : (
                                                        <Users className="w-5 h-5 text-gray-500" />
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <p className="font-medium">{u.name}</p>
                                                        {u.subscription_tier === 'premium' && (
                                                            <Badge className={`text-white text-xs ${u.premium_expired ? 'bg-red-500' : 'bg-amber-500'}`}>
                                                                {u.premium_expired ? 'Premium Expiré' : 'Premium'}
                                                                {u.subscription_type === 'gift' && !u.premium_expired && ' (Cadeau)'}
                                                            </Badge>
                                                        )}
                                                        {(u.is_admin || u.email === 'mmecameleon@hotmail.com') && (
                                                            <Badge className={`text-white text-xs ${u.email === 'mmecameleon@hotmail.com' ? 'bg-purple-600' : 'bg-purple-500'}`}>
                                                                {u.email === 'mmecameleon@hotmail.com' ? 'Super Admin' : 'Admin'}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground">{u.email}</p>
                                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                        <span>Inscrit le {new Date(u.created_at).toLocaleDateString('fr-CA')}</span>
                                                        {u.subscription_type === 'gift' && u.premium_expires_at && (
                                                            <span className={`${u.premium_expired ? 'text-red-600' : 'text-amber-600'}`}>
                                                                • {u.premium_expired ? 'Expiré le' : 'Expire le'} {new Date(u.premium_expires_at).toLocaleDateString('fr-CA')}
                                                            </span>
                                                        )}
                                                        {u.subscription_type === 'gift' && !u.premium_expires_at && u.subscription_tier === 'premium' && (
                                                            <span className="text-green-600">• Illimité</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {/* Admin toggle button - only for super admin */}
                                                {u.email !== 'mmecameleon@hotmail.com' && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleToggleAdmin(u.id, u.is_admin)}
                                                        disabled={togglingAdmin === u.id}
                                                        className={u.is_admin ? "text-purple-600 border-purple-300 hover:bg-purple-50" : "text-gray-600 border-gray-300 hover:bg-gray-50"}
                                                    >
                                                        {togglingAdmin === u.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <>
                                                                <Shield className="w-4 h-4 mr-1" />
                                                                {u.is_admin ? 'Retirer Admin' : 'Rendre Admin'}
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                                {u.subscription_tier === 'premium' && !u.premium_expired ? (
                                                    u.subscription_type === 'gift' && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleRevokePremium(u.id)}
                                                            disabled={togglingPremium === u.id}
                                                            className="text-red-600 border-red-300 hover:bg-red-50"
                                                        >
                                                            {togglingPremium === u.id ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <>
                                                                    <XCircle className="w-4 h-4 mr-1" />
                                                                    Retirer
                                                                </>
                                                            )}
                                                        </Button>
                                                    )
                                                ) : (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => { setShowPremiumModal(u.id); setSelectedDuration(30); }}
                                                        disabled={togglingPremium === u.id}
                                                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                                    >
                                                        {togglingPremium === u.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <>
                                                                <Crown className="w-4 h-4 mr-1" />
                                                                {u.premium_expired ? 'Renouveler' : 'Offrir Premium'}
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    {users.length === 0 && (
                                        <p className="text-center text-muted-foreground py-8">Aucun utilisateur inscrit</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Grocery List Editor Modal */}
            {editingGroceryList && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <ShoppingCart className="w-8 h-8" />
                                    <div>
                                        <h2 className="font-heading text-2xl font-bold">Modifier les correspondances</h2>
                                        <p className="text-white/80">Associez manuellement les ingrédients aux rabais</p>
                                    </div>
                                </div>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => setEditingGroceryList(null)}
                                    className="text-white hover:bg-white/20"
                                >
                                    <X className="w-6 h-6" />
                                </Button>
                            </div>
                            <div className="mt-4 bg-white/20 rounded-xl p-3 flex items-center justify-between">
                                <span>💰 Économies totales</span>
                                <span className="font-heading text-2xl font-bold">{editingGroceryList.totalSavings?.toFixed(2) || '0.00'}$</span>
                            </div>
                        </div>
                        
                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            <div className="space-y-3">
                                {editingGroceryList.groceryList.map((item, index) => (
                                    <div 
                                        key={index} 
                                        className={`p-4 rounded-xl border-2 ${item.has_deal ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}
                                    >
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex-1">
                                                <span className={`font-semibold ${item.has_deal ? 'text-orange-700' : 'text-gray-800'}`}>
                                                    {item.item}
                                                </span>
                                                <span className="text-gray-500 ml-2 text-sm">({item.quantity})</span>
                                                {item.deal_info?.manual && (
                                                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Manuel</span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <select
                                                    className="border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:ring-0 min-w-[250px]"
                                                    value={item.deal_info?.deal_id || ''}
                                                    onChange={(e) => handleUpdateGroceryItemDeal(item.item, e.target.value || null)}
                                                >
                                                    <option value="">-- Aucun rabais --</option>
                                                    {editingGroceryList.availableDeals.map((deal) => (
                                                        <option key={deal.id} value={deal.id}>
                                                            {deal.product_name} (-{deal.discount_percent}%) @ {deal.store_name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {item.has_deal && item.deal_info && (
                                                    <span className="text-green-600 font-bold whitespace-nowrap">
                                                        -{item.deal_info.savings?.toFixed(2)}$
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {item.has_deal && item.deal_info && (
                                            <div className="mt-2 text-sm text-orange-600 flex items-center gap-2">
                                                <Tag className="w-4 h-4" />
                                                {item.deal_info.product_name} @ {item.deal_info.store_name}
                                                {item.deal_info.match_score && !item.deal_info.manual && (
                                                    <span className="text-gray-500">(Score: {item.deal_info.match_score.toFixed(0)}%)</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div className="border-t p-4 bg-gray-50 flex justify-end gap-3">
                            <Button variant="outline" onClick={() => setEditingGroceryList(null)}>
                                Fermer
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Grocery List Editor Modal */}
            {showGroceryEditor && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50">
                            <h3 className="font-heading font-bold text-xl flex items-center gap-2">
                                <ShoppingCart className="w-5 h-5 text-green-600" />
                                Modifier la liste d&apos;épicerie
                            </h3>
                            <Button variant="ghost" size="sm" onClick={() => setShowGroceryEditor(false)}>
                                <X className="w-5 h-5" />
                            </Button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {groceryListData.grocery_list.map((item, index) => (
                                <div key={index} className={`border rounded-xl p-3 ${item.has_deal ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}>
                                    <div className="flex gap-2 items-start">
                                        <div className="flex-1 grid grid-cols-12 gap-2">
                                            <div className="col-span-5">
                                                <input
                                                    type="text"
                                                    className="w-full border rounded-lg px-3 py-2 text-sm"
                                                    placeholder="Nom de l&apos;ingrédient"
                                                    value={item.item}
                                                    onChange={(e) => updateGroceryItem(index, 'item', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <input
                                                    type="text"
                                                    className="w-full border rounded-lg px-3 py-2 text-sm"
                                                    placeholder="Qté"
                                                    value={item.quantity}
                                                    onChange={(e) => updateGroceryItem(index, 'quantity', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    className="w-full border rounded-lg px-3 py-2 text-sm"
                                                    placeholder="Coût $"
                                                    value={item.estimated_cost || ''}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(',', '.');
                                                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                                            updateGroceryItem(index, 'estimated_cost', value);
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div className="col-span-3">
                                                <select
                                                    className="w-full border rounded-lg px-2 py-2 text-sm"
                                                    value={item.deal_info?.deal_id || ''}
                                                    onChange={(e) => setGroceryItemDeal(index, e.target.value)}
                                                >
                                                    <option value="">Aucun rabais</option>
                                                    {groceryListData.available_deals.map(deal => (
                                                        <option key={deal.id} value={deal.id}>
                                                            {deal.product_name} (-{deal.discount_percent}% @ {deal.store_name})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => removeGroceryItem(index)}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    {item.deal_info && (
                                        <div className="mt-2 text-xs text-green-700 flex items-center gap-2">
                                            <Tag className="w-3 h-3" />
                                            <span className="font-medium">{item.deal_info.product_name}</span>
                                            <span>•</span>
                                            <span>{item.deal_info.sale_price?.toFixed(2)}$ @ {item.deal_info.store_name}</span>
                                            <span>•</span>
                                            <span className="text-green-600 font-bold">Économie: {item.deal_info.savings?.toFixed(2)}$</span>
                                            {item.deal_info.manual && (
                                                <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-[10px] font-medium">Manuel</span>
                                            )}
                                        </div>
                                    )}
                                    {item.recipes?.length > 0 && (
                                        <div className="mt-1 text-xs text-gray-500">
                                            Recettes: {item.recipes.join(', ')}
                                        </div>
                                    )}
                                </div>
                            ))}
                            
                            <Button
                                variant="outline"
                                className="w-full border-dashed border-2 text-gray-500 hover:text-gray-700"
                                onClick={addGroceryItem}
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Ajouter un ingrédient
                            </Button>
                        </div>
                        
                        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                <span className="font-medium">{groceryListData.grocery_list.length}</span> ingrédients
                                <span className="mx-2">•</span>
                                <span className="text-green-600 font-medium">
                                    Économies: {groceryListData.grocery_list.reduce((sum, item) => sum + (item.deal_info?.savings || 0), 0).toFixed(2)}$
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setShowGroceryEditor(false)}>
                                    Annuler
                                </Button>
                                <Button 
                                    className="bg-green-600 text-white hover:bg-green-700"
                                    onClick={handleSaveGroceryList}
                                    disabled={savingGroceryList}
                                >
                                    {savingGroceryList ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4 mr-2" />
                                    )}
                                    Sauvegarder
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ==================== ITEM COMPONENTS ====================

const RecipeItem = ({ recipe, onEdit, onDelete, onApprove, onReject, showModeration }) => (
    <div className={`flex items-center justify-between p-4 rounded-xl mb-2 ${recipe.status === 'pending' ? 'bg-orange-50 border border-orange-200' : 'bg-muted/50'}`}>
        <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
                <Badge variant={recipe.status === 'pending' ? 'outline' : 'secondary'}>
                    {recipe.category}
                </Badge>
                <span className="font-semibold">{recipe.title}</span>
                {recipe.is_premium && <Crown className="w-4 h-4 text-yellow-500" />}
            </div>
            <p className="text-sm text-muted-foreground">
                Par {recipe.author_name} • {recipe.cost_per_serving?.toFixed(2)}$/portion • {recipe.prep_time + recipe.cook_time} min
            </p>
        </div>
        <div className="flex gap-2">
            {showModeration && (
                <>
                    <Button variant="outline" size="sm" onClick={onApprove} className="text-green-600" title="Approuver">
                        <Check className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={onReject} className="text-red-600" title="Rejeter">
                        <XCircle className="w-4 h-4" />
                    </Button>
                </>
            )}
            <Button variant="outline" size="sm" onClick={onEdit} title="Modifier">
                <Edit className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={onDelete} className="text-destructive" title="Supprimer">
                <Trash2 className="w-4 h-4" />
            </Button>
        </div>
    </div>
);

const TipItem = ({ tip, onEdit, onDelete, onApprove, onReject, showModeration }) => (
    <div className={`flex items-center justify-between p-4 rounded-xl mb-2 ${tip.status === 'pending' ? 'bg-orange-50 border border-orange-200' : 'bg-muted/50'}`}>
        <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
                <Badge variant={tip.status === 'pending' ? 'outline' : 'secondary'}>
                    {tip.category}
                </Badge>
                <span className="font-semibold">{tip.title}</span>
                {tip.is_premium && <Crown className="w-4 h-4 text-yellow-500" />}
            </div>
            <p className="text-sm text-muted-foreground">Par {tip.author_name}</p>
        </div>
        <div className="flex gap-2">
            {showModeration && (
                <>
                    <Button variant="outline" size="sm" onClick={onApprove} className="text-green-600" title="Approuver">
                        <Check className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={onReject} className="text-red-600" title="Rejeter">
                        <XCircle className="w-4 h-4" />
                    </Button>
                </>
            )}
            <Button variant="outline" size="sm" onClick={onEdit} title="Modifier">
                <Edit className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={onDelete} className="text-destructive" title="Supprimer">
                <Trash2 className="w-4 h-4" />
            </Button>
        </div>
    </div>
);

const DealItem = ({ deal, onEdit, onDelete, onTogglePublish, isToggling }) => (
    <div className={`flex items-center justify-between p-4 rounded-xl mb-2 ${deal.is_published !== false ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
        <div className="flex items-center gap-4">
            {deal.image_url && (
                <img src={deal.image_url} alt={deal.product_name} className="w-16 h-16 object-cover rounded-lg" />
            )}
            <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="destructive">-{deal.discount_percent}%</Badge>
                    <span className="font-semibold">{deal.product_name}</span>
                    {deal.is_published !== false ? (
                        <Badge className="bg-green-500 text-white text-xs">✓ Publié</Badge>
                    ) : (
                        <Badge className="bg-orange-500 text-white text-xs">Brouillon</Badge>
                    )}
                </div>
                <p className="text-sm text-muted-foreground">
                    {deal.store_name} • {deal.sale_price?.toFixed(2)}$ <span className="line-through">{deal.original_price?.toFixed(2)}$</span>
                </p>
            </div>
        </div>
        <div className="flex gap-2 flex-wrap">
            <Button 
                variant={deal.is_published !== false ? "outline" : "default"}
                size="sm" 
                onClick={onTogglePublish}
                disabled={isToggling}
                className={deal.is_published !== false ? "text-orange-600 hover:bg-orange-50 border-orange-300" : "bg-green-600 hover:bg-green-700 text-white"}
                title={deal.is_published !== false ? "Dépublier" : "Publier"}
            >
                {isToggling ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : deal.is_published !== false ? (
                    <EyeOff className="w-4 h-4" />
                ) : (
                    <Eye className="w-4 h-4" />
                )}
            </Button>
            <Button variant="outline" size="sm" onClick={onEdit} title="Modifier">
                <Edit className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={onDelete} className="text-destructive" title="Supprimer">
                <Trash2 className="w-4 h-4" />
            </Button>
        </div>
    </div>
);

// ==================== FORM COMPONENTS ====================

const PackForm = ({ pack, onSave, onCancel, onRefresh, isDuplicate = false, availableRecipes = [], availableDeals = [], onGenerateRecommendations, generatingRecommendations = false }) => {
    const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
    
    // Get all approved recipes (no category filter)
    const availableRecipesForPack = availableRecipes;
    
    // State for showing deals panel
    const [showDealsPanel, setShowDealsPanel] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    
    const [formData, setFormData] = useState({
        pack_number: pack?.pack_number || 1,
        week_number: pack?.week_number || 1,
        year: pack?.year || new Date().getFullYear(),
        title: pack?.title || '',
        description: pack?.description || '',
        intro_message: pack?.intro_message || '',
        how_to_use: pack?.how_to_use || [''],
        grocery_list: pack?.grocery_list || [{ item: '', quantity: '', estimated_cost: '' }],
        // Store recipe IDs for each day
        recipe_ids: pack?.recipe_ids || {
            'Lundi': '',
            'Mardi': '',
            'Mercredi': '',
            'Jeudi': '',
            'Vendredi': ''
        },
        freezing_tips: pack?.freezing_tips || [''],
        emergency_recipe: pack?.emergency_recipe || { title: '', description: '', ingredients: '', steps: '', time: '', cost: '' },
        recommendations: pack?.recommendations || '',
        closing_message: pack?.closing_message || '',
        total_cost: pack?.total_cost || 0,
        savings_estimate: pack?.savings_estimate || 0,
        send_notification: false, // New: send email notification to premium users
        // Article section with recipe links support [[recipe:ID|text]]
        article_title: pack?.article_title || '',
        article_content: pack?.article_content || '',
        ...(pack?.id && !isDuplicate && { id: pack.id })
    });

    // Calculate total cost based on selected recipes
    const calculateTotalCost = () => {
        let total = 0;
        Object.values(formData.recipe_ids).forEach(recipeId => {
            if (recipeId) {
                const recipe = availableRecipes.find(r => r.id === recipeId);
                if (recipe) {
                    total += (recipe.cost_per_serving || 0) * (recipe.servings || 4);
                }
            }
        });
        // Add grocery list items
        formData.grocery_list.forEach(item => {
            total += parseFloat(item.estimated_cost) || 0;
        });
        return total;
    };

    const handleRecipeSelect = (day, recipeId) => {
        setFormData(prev => ({
            ...prev,
            recipe_ids: {
                ...prev.recipe_ids,
                [day]: recipeId
            }
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Build soupers array from selected recipes
        const soupers = days.map(day => {
            const recipeId = formData.recipe_ids[day];
            const recipe = availableRecipes.find(r => r.id === recipeId);
            return {
                day,
                recipe_id: recipeId || null,
                title: recipe?.title || '',
                prep_time: recipe?.prep_time || 0,
                cook_time: recipe?.cook_time || 0,
                cost_per_serving: recipe?.cost_per_serving || 0,
                servings: recipe?.servings || 4
            };
        });
        
        onSave({ 
            ...formData, 
            soupers,
            total_cost: calculateTotalCost()
        });
    };

    const getSelectedRecipe = (day) => {
        const recipeId = formData.recipe_ids[day];
        return availableRecipes.find(r => r.id === recipeId);
    };

    // Get all ingredients from selected recipes
    const getSelectedRecipesIngredients = () => {
        const ingredients = [];
        Object.values(formData.recipe_ids).forEach(recipeId => {
            if (recipeId) {
                const recipe = availableRecipes.find(r => r.id === recipeId);
                if (recipe?.ingredients) {
                    recipe.ingredients.forEach(ing => {
                        ingredients.push({
                            ...ing,
                            recipeTitle: recipe.title,
                            recipeId: recipe.id
                        });
                    });
                }
            }
        });
        return ingredients;
    };

    // Find matches between ingredients and deals
    // Helper function for smarter ingredient-deal matching
    const normalizeForMatching = (text) => {
        // Remove accents
        const withoutAccents = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        // Convert to lowercase
        const lower = withoutAccents.toLowerCase();
        // Remove common articles and prepositions
        const stopWords = ['le', 'la', 'les', 'de', 'du', 'des', 'un', 'une', 'au', 'aux', 'en', 'pour', 'avec', 'sans', 'et', 'ou', 'a', 'the', 'of', 'and', 'or'];
        const words = lower.split(/[\s,\-_'()]+/).filter(w => w.length > 2 && !stopWords.includes(w));
        return words;
    };

    const checkIngredientDealMatch = (ingredientName, dealName) => {
        const ingWords = normalizeForMatching(ingredientName);
        const dealWords = normalizeForMatching(dealName);
        
        // Check if any significant word from deal matches any word from ingredient
        for (const dealWord of dealWords) {
            for (const ingWord of ingWords) {
                // Exact match
                if (dealWord === ingWord) return true;
                // One contains the other (for plurals, variations)
                if (dealWord.length >= 4 && ingWord.length >= 4) {
                    if (dealWord.includes(ingWord) || ingWord.includes(dealWord)) return true;
                    // Check if words share same root (first 4+ chars)
                    const minLen = Math.min(dealWord.length, ingWord.length);
                    if (minLen >= 4) {
                        const rootLen = Math.max(4, Math.floor(minLen * 0.7));
                        if (dealWord.substring(0, rootLen) === ingWord.substring(0, rootLen)) return true;
                    }
                }
            }
        }
        return false;
    };

    const findDealMatches = () => {
        const ingredients = getSelectedRecipesIngredients();
        const matches = [];
        
        // Only include published deals
        const publishedDeals = availableDeals.filter(d => d.is_published !== false);
        
        publishedDeals.forEach(deal => {
            const matchingIngredients = ingredients.filter(ing => 
                checkIngredientDealMatch(ing.name, deal.product_name)
            );
            
            if (matchingIngredients.length > 0) {
                matches.push({
                    deal,
                    ingredients: matchingIngredients
                });
            }
        });
        
        return matches;
    };

    // Get deals without matches (for awareness) - only published
    const getUnmatchedDeals = () => {
        const matches = findDealMatches();
        const matchedDealIds = matches.map(m => m.deal.id);
        return availableDeals.filter(d => d.is_published !== false && !matchedDealIds.includes(d.id));
    };

    const dealMatches = findDealMatches();
    const unmatchedDeals = getUnmatchedDeals();

    // Handle refresh
    const handleRefresh = async () => {
        if (onRefresh) {
            setIsRefreshing(true);
            await onRefresh();
            setIsRefreshing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6 border rounded-xl p-6 mb-6 bg-white">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-heading font-bold text-xl">
                        {isDuplicate ? 'Dupliquer le Pack' : pack?.id ? 'Modifier le Pack' : 'Nouveau Pack'}
                    </h3>
                    {isDuplicate && <p className="text-sm text-blue-600 mt-1">Créer un nouveau pack basé sur un existant</p>}
                </div>
                <div className="flex items-center gap-2">
                    <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    >
                        <RefreshCw className={`w-4 h-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
                        {isRefreshing ? 'Chargement...' : 'Rafraîchir'}
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={onCancel}><X className="w-4 h-4" /></Button>
                </div>
            </div>

            {/* Basic Info */}
            <div className="grid sm:grid-cols-4 gap-4">
                <div>
                    <Label>Pack #</Label>
                    <Input type="number" value={formData.pack_number} onChange={(e) => setFormData(prev => ({ ...prev, pack_number: parseInt(e.target.value) }))} />
                </div>
                <div>
                    <Label>Semaine</Label>
                    <Input type="number" value={formData.week_number} onChange={(e) => setFormData(prev => ({ ...prev, week_number: parseInt(e.target.value) }))} />
                </div>
                <div>
                    <Label>Année</Label>
                    <Input type="number" value={formData.year} onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))} />
                </div>
                <div>
                    <Label>Économies ($)</Label>
                    <Input 
                        type="text" 
                        inputMode="decimal" 
                        placeholder="0.00"
                        value={formData.savings_estimate} 
                        onChange={(e) => {
                            const value = e.target.value.replace(',', '.');
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                setFormData(prev => ({ ...prev, savings_estimate: value }));
                            }
                        }}
                    />
                </div>
            </div>

            <div>
                <Label>Titre</Label>
                <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="Semaine tough" />
            </div>

            <div>
                <Label>Sous-titre</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Description courte" />
            </div>

            <div>
                <Label>Message d&apos;accueil</Label>
                <Textarea value={formData.intro_message} onChange={(e) => setFormData(prev => ({ ...prev, intro_message: e.target.value }))} />
            </div>

            {/* Recipe Selection for each day */}
            <div className="border rounded-lg p-4 bg-muted/30">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <ChefHat className="w-5 h-5" />
                    5 Soupers de la Semaine
                </h4>
                
                {availableRecipesForPack.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                        <p>Aucune recette de type Souper disponible.</p>
                        <p className="text-sm mt-2">Créez d&apos;abord des recettes dans l&apos;onglet Recettes avec la catégorie Souper.</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {days.map((day) => {
                            const selectedRecipe = getSelectedRecipe(day);
                            return (
                                <div key={day} className="flex items-center gap-4 p-3 bg-white rounded-lg border">
                                    <div className="w-24 font-medium text-primary">{day}</div>
                                    <div className="flex-1">
                                        <select
                                            className="w-full border rounded-md p-2"
                                            value={formData.recipe_ids[day] || ''}
                                            onChange={(e) => handleRecipeSelect(day, e.target.value)}
                                        >
                                            <option value="">-- Sélectionner une recette --</option>
                                            {availableRecipesForPack.map(recipe => (
                                                <option key={recipe.id} value={recipe.id}>
                                                    {recipe.title} ({recipe.cost_per_serving?.toFixed(2)}$/portion, {recipe.prep_time + recipe.cook_time} min)
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {selectedRecipe && (
                                        <div className="text-sm text-muted-foreground">
                                            <Badge variant="outline">{selectedRecipe.difficulty}</Badge>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {/* Show estimated total from selected recipes */}
                <div className="mt-4 pt-4 border-t flex justify-between items-center">
                    <span className="font-medium">Coût total estimé:</span>
                    <span className="text-xl font-bold text-primary">{calculateTotalCost().toFixed(2)}$</span>
                </div>
            </div>

            {/* Deals Matching Section */}
            {availableDeals.length > 0 && (
                <div className="space-y-3 border rounded-xl p-4 bg-gradient-to-r from-red-50 to-orange-50">
                    <div className="flex items-center justify-between">
                        <Label className="font-bold flex items-center gap-2">
                            <Tag className="w-4 h-4 text-red-600" />
                            Rabais de la semaine
                            {dealMatches.length > 0 && (
                                <Badge className="bg-green-500 text-white ml-2">
                                    {dealMatches.length} correspondance{dealMatches.length > 1 ? 's' : ''}
                                </Badge>
                            )}
                        </Label>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowDealsPanel(!showDealsPanel)}
                            className="bg-white hover:bg-red-50 border-red-200"
                        >
                            {showDealsPanel ? (
                                <>
                                    <X className="w-4 h-4 mr-1" />
                                    Masquer
                                </>
                            ) : (
                                <>
                                    <Tag className="w-4 h-4 mr-1" />
                                    Voir les {availableDeals.filter(d => d.is_published !== false).length} rabais
                                </>
                            )}
                        </Button>
                    </div>
                    
                    {/* Quick summary when panel is closed */}
                    {!showDealsPanel && dealMatches.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                            {dealMatches.slice(0, 5).map(match => (
                                <div key={match.deal.id} className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded-full text-xs">
                                    <Check className="w-3 h-3 text-green-600" />
                                    <span className="font-medium text-green-800">{match.deal.product_name}</span>
                                    <span className="text-green-600">-{match.deal.discount_percent}%</span>
                                </div>
                            ))}
                            {dealMatches.length > 5 && (
                                <span className="text-xs text-muted-foreground">+{dealMatches.length - 5} autres</span>
                            )}
                        </div>
                    )}
                    
                    {/* Expanded deals panel */}
                    {showDealsPanel && (
                        <div className="mt-3 space-y-4">
                            {/* Matched deals */}
                            {dealMatches.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-bold text-green-700 mb-2 flex items-center gap-1">
                                        <Check className="w-4 h-4" />
                                        Rabais correspondant aux ingrédients
                                    </h4>
                                    <div className="grid gap-2">
                                        {dealMatches.map(match => (
                                            <div key={match.deal.id} className="p-3 bg-green-100 rounded-lg border border-green-200">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-bold text-green-800">{match.deal.product_name}</span>
                                                        <Badge className="bg-red-500 text-white">-{match.deal.discount_percent}%</Badge>
                                                    </div>
                                                    <div className="text-sm">
                                                        <span className="line-through text-gray-500">{match.deal.original_price?.toFixed(2)}$</span>
                                                        <span className="font-bold text-green-700 ml-2">{match.deal.sale_price?.toFixed(2)}$</span>
                                                    </div>
                                                </div>
                                                <div className="text-xs text-green-700">
                                                    <span className="font-medium">Utilisé dans:</span>
                                                    {match.ingredients.map((ing, idx) => (
                                                        <span key={idx} className="ml-1">
                                                            {ing.recipeTitle} ({ing.name}){idx < match.ingredients.length - 1 ? ',' : ''}
                                                        </span>
                                                    ))}
                                                </div>
                                                <div className="text-xs text-gray-600 mt-1">
                                                    📍 {match.deal.store_name}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Unmatched deals - opportunity */}
                            {unmatchedDeals.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-bold text-orange-700 mb-2 flex items-center gap-1">
                                        <AlertCircle className="w-4 h-4" />
                                        Autres rabais disponibles
                                        <span className="font-normal text-xs text-gray-500 ml-1">(pas encore utilisés)</span>
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {unmatchedDeals.map(deal => (
                                            <div key={deal.id} className="px-3 py-2 bg-orange-100 rounded-lg border border-orange-200 text-sm">
                                                <span className="font-medium text-orange-800">{deal.product_name}</span>
                                                <Badge className="bg-orange-500 text-white ml-2 text-xs">-{deal.discount_percent}%</Badge>
                                                <span className="text-xs text-gray-600 block mt-1">
                                                    {deal.sale_price?.toFixed(2)}$ @ {deal.store_name}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-orange-600 mt-2 italic">
                                        💡 Considérez ajouter des recettes utilisant ces ingrédients pour maximiser les économies
                                    </p>
                                </div>
                            )}
                            
                            {availableDeals.filter(d => d.is_published !== false).length === 0 && (
                                <p className="text-center text-gray-500 py-4">
                                    Aucun rabais publié cette semaine
                                </p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Recommendations Section */}
            <div className="space-y-3 border rounded-xl p-4 bg-purple-50">
                <div className="flex items-center justify-between">
                    <Label className="font-bold flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        Mes recommandations de la semaine
                    </Label>
                    {pack?.id && onGenerateRecommendations && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onGenerateRecommendations(pack.id, (recommendations) => {
                                setFormData(prev => ({ ...prev, recommendations }));
                            })}
                            disabled={generatingRecommendations}
                            className="bg-purple-100 hover:bg-purple-200 border-purple-300"
                        >
                            {generatingRecommendations ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                    Génération...
                                </>
                            ) : (
                                <>
                                    <Sparkles className="w-4 h-4 mr-1" />
                                    Générer avec IA
                                </>
                            )}
                        </Button>
                    )}
                </div>
                <textarea
                    className="w-full border rounded-lg p-3 min-h-[100px]"
                    placeholder="Partagez vos conseils, suggestions et recommandations pour ce pack..."
                    value={formData.recommendations}
                    onChange={(e) => setFormData(prev => ({ ...prev, recommendations: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">
                    {pack?.id 
                        ? "Cliquez sur 'Générer avec IA' pour créer automatiquement des recommandations basées sur les rabais de la semaine."
                        : "Sauvegardez le pack d'abord, puis vous pourrez générer les recommandations avec l'IA."
                    }
                </p>
            </div>

            {/* Article Section */}
            <div className="space-y-3 border rounded-xl p-4 bg-purple-50">
                <Label className="font-bold flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-purple-600" />
                    Article du pack (optionnel)
                </Label>
                <p className="text-xs text-muted-foreground">
                    Rédigez un article qui sera affiché dans le pack. Vous pouvez ajouter des liens vers des recettes avec le format: <code className="bg-purple-100 px-1 rounded">[[recipe:ID|texte à afficher]]</code>
                </p>
                <div className="space-y-2">
                    <input
                        type="text"
                        className="w-full border rounded-lg p-2"
                        placeholder="Titre de l'article (ex: Les secrets d'une bonne sauce bolognaise)"
                        value={formData.article_title}
                        onChange={(e) => setFormData(prev => ({ ...prev, article_title: e.target.value }))}
                    />
                    <textarea
                        className="w-full border rounded-lg p-3 min-h-[150px] font-mono text-sm"
                        placeholder="Contenu de l'article...

Exemple avec lien vers une recette:
Découvrez notre délicieuse [[recipe:abc123|recette de poulet rôti]] qui accompagne parfaitement ce pack!"
                        value={formData.article_content}
                        onChange={(e) => setFormData(prev => ({ ...prev, article_content: e.target.value }))}
                    />
                </div>
                {/* Recipe ID helper */}
                {availableRecipes.length > 0 && (
                    <details className="text-xs">
                        <summary className="cursor-pointer text-purple-600 hover:text-purple-800">
                            Voir les IDs des recettes disponibles
                        </summary>
                        <div className="mt-2 max-h-32 overflow-y-auto bg-white rounded p-2 border">
                            {availableRecipes.slice(0, 20).map(r => (
                                <div key={r.id} className="flex justify-between py-0.5 border-b border-gray-100 last:border-0">
                                    <span className="truncate mr-2">{r.title}</span>
                                    <code className="text-purple-600 select-all">{r.id}</code>
                                </div>
                            ))}
                        </div>
                    </details>
                )}
            </div>

            {/* Freezing Tips Section */}
            <div className="space-y-3 border rounded-xl p-4 bg-blue-50">
                <div className="flex items-center justify-between">
                    <Label className="font-bold flex items-center gap-2">
                        <Snowflake className="w-4 h-4 text-blue-600" />
                        Astuces congélation
                    </Label>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData(prev => ({
                            ...prev,
                            freezing_tips: [...prev.freezing_tips, '']
                        }))}
                    >
                        <Plus className="w-4 h-4 mr-1" /> Ajouter
                    </Button>
                </div>
                <div className="space-y-2">
                    {formData.freezing_tips.map((tip, index) => (
                        <div key={index} className="flex gap-2">
                            <input
                                type="text"
                                className="flex-1 border rounded-lg p-2"
                                placeholder={`Astuce ${index + 1}: ex. Les sauces se congèlent très bien...`}
                                value={tip}
                                onChange={(e) => {
                                    const newTips = [...formData.freezing_tips];
                                    newTips[index] = e.target.value;
                                    setFormData(prev => ({ ...prev, freezing_tips: newTips }));
                                }}
                            />
                            {formData.freezing_tips.length > 1 && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        const newTips = formData.freezing_tips.filter((_, i) => i !== index);
                                        setFormData(prev => ({ ...prev, freezing_tips: newTips }));
                                    }}
                                >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Emergency Recipe Section */}
            <div className="space-y-3 border rounded-xl p-4 bg-orange-50">
                <Label className="font-bold flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-orange-600" />
                    Recette secours (repas de dépannage)
                </Label>
                <div className="grid gap-3">
                    <input
                        type="text"
                        className="border rounded-lg p-2"
                        placeholder="Titre de la recette secours"
                        value={formData.emergency_recipe.title}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            emergency_recipe: { ...prev.emergency_recipe, title: e.target.value }
                        }))}
                    />
                    <textarea
                        className="border rounded-lg p-2 min-h-[60px]"
                        placeholder="Description (ex: Quand vous n'avez pas le temps, cette recette simple vous sauvera!)"
                        value={formData.emergency_recipe.description}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            emergency_recipe: { ...prev.emergency_recipe, description: e.target.value }
                        }))}
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <input
                            type="text"
                            className="border rounded-lg p-2"
                            placeholder="Temps total (ex: 15 min)"
                            value={formData.emergency_recipe.time}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                emergency_recipe: { ...prev.emergency_recipe, time: e.target.value }
                            }))}
                        />
                        <input
                            type="text"
                            className="border rounded-lg p-2"
                            placeholder="Coût estimé (ex: 5$)"
                            value={formData.emergency_recipe.cost}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                emergency_recipe: { ...prev.emergency_recipe, cost: e.target.value }
                            }))}
                        />
                    </div>
                    <textarea
                        className="border rounded-lg p-2 min-h-[80px]"
                        placeholder="Ingrédients (un par ligne)"
                        value={formData.emergency_recipe.ingredients}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            emergency_recipe: { ...prev.emergency_recipe, ingredients: e.target.value }
                        }))}
                    />
                    <textarea
                        className="border rounded-lg p-2 min-h-[80px]"
                        placeholder="Étapes de préparation (une par ligne)"
                        value={formData.emergency_recipe.steps}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            emergency_recipe: { ...prev.emergency_recipe, steps: e.target.value }
                        }))}
                    />
                </div>
            </div>

            {/* Notification checkbox - only show for new packs */}
            {!pack?.id && (
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                    <input
                        type="checkbox"
                        id="send_notification"
                        checked={formData.send_notification}
                        onChange={(e) => setFormData(prev => ({ ...prev, send_notification: e.target.checked }))}
                        className="w-5 h-5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label htmlFor="send_notification" className="flex-1 cursor-pointer">
                        <span className="font-medium text-purple-800">📧 Envoyer une notification par email</span>
                        <p className="text-sm text-purple-600">Tous les membres Premium recevront un email les informant du nouveau pack</p>
                    </label>
                </div>
            )}

            <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
                <Button type="submit" className="bg-primary text-white"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
            </div>
        </form>
    );
};

const RecipeForm = ({ recipe, onSave, onCancel, availableRecipes = [] }) => {
    const [formData, setFormData] = useState({
        title: recipe?.title || '',
        description: recipe?.description || '',
        category: recipe?.category || 'dinner',
        categories: recipe?.categories || [recipe?.category || 'dinner'],
        ingredients: recipe?.ingredients || [{ name: '', quantity: '', unit: 'tasse', estimated_cost: '', linked_recipe_id: '' }],
        steps: recipe?.steps || [''],
        prep_time: recipe?.prep_time || 30,
        cook_time: recipe?.cook_time || 30,
        servings: recipe?.servings || 4,
        cost_per_serving: recipe?.cost_per_serving || 0,
        image_url: recipe?.image_url || '',
        tags: Array.isArray(recipe?.tags) ? recipe.tags.join(', ') : (recipe?.tags || ''),
        difficulty: recipe?.difficulty || 'facile',
        is_premium: recipe?.is_premium || false,
        tip: recipe?.tip || '',
        source_name: recipe?.source_name || '',
        source_url: recipe?.source_url || '',
        solo_tips: recipe?.solo_tips || [''],
        variants: recipe?.variants || [],
        household_sizes: recipe?.household_sizes || [],
        protein_type: recipe?.protein_type || '',
        freezable: recipe?.freezable || false,
        ...(recipe?.id && { id: recipe.id }),
        ...(recipe?.author_id && { author_id: recipe.author_id }),
        ...(recipe?.author_name && { author_name: recipe.author_name }),
        ...(recipe?.status && { status: recipe.status }),
        ...(recipe?.created_at && { created_at: recipe.created_at })
    });
    
    // Household size options
    const householdSizeOptions = [
        { value: 'solo', label: 'Solo', icon: '👤' },
        { value: 'couple', label: 'Couple', icon: '👫' },
        { value: 'family', label: 'Famille', icon: '👨‍👩‍👧' },
        { value: 'large_family', label: 'Grande famille', icon: '👨‍👩‍👧‍👦' }
    ];
    
    // Autocomplete state for ingredient search (Premium feature)
    const [activeIngredientIndex, setActiveIngredientIndex] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [searchingRecipes, setSearchingRecipes] = useState(false);

    // Search for recipes to link as ingredient
    const searchRecipesForLinking = async (query, index) => {
        if (query.length < 2) {
            setSuggestions([]);
            return;
        }
        
        setSearchingRecipes(true);
        try {
            const response = await api.searchRecipesForLinking(query);
            setSuggestions(response.data);
            setActiveIngredientIndex(index);
        } catch (error) {
            // User is not premium or error occurred
            setSuggestions([]);
        } finally {
            setSearchingRecipes(false);
        }
    };

    // Select a suggestion
    const selectSuggestion = (index, suggestion) => {
        setFormData(prev => ({
            ...prev,
            ingredients: prev.ingredients.map((ing, i) => 
                i === index ? { 
                    ...ing, 
                    name: suggestion.title, 
                    linked_recipe_id: suggestion.id 
                } : ing
            )
        }));
        setSuggestions([]);
        setActiveIngredientIndex(null);
    };

    // Auto-recalculate cost per serving when ingredients change
    useEffect(() => {
        const totalCost = formData.ingredients.reduce((sum, ing) => sum + (parseFloat(ing.estimated_cost) || 0), 0);
        const newCostPerServing = formData.servings > 0 ? (totalCost / formData.servings).toFixed(2) : '0.00';
        
        // Only update if different to avoid infinite loop
        if (newCostPerServing !== String(formData.cost_per_serving)) {
            setFormData(prev => ({ ...prev, cost_per_serving: newCostPerServing }));
        }
    }, [formData.ingredients, formData.servings, formData.cost_per_serving]);

    // Ingredient handlers
    const addIngredient = () => {
        setFormData(prev => ({
            ...prev,
            ingredients: [...prev.ingredients, { name: '', quantity: '', unit: 'tasse', estimated_cost: '', linked_recipe_id: '' }]
        }));
    };

    const removeIngredient = (index) => {
        setFormData(prev => {
            const newIngredients = prev.ingredients.filter((_, i) => i !== index);
            const totalCost = newIngredients.reduce((sum, ing) => sum + (parseFloat(ing.estimated_cost) || 0), 0);
            const newCostPerServing = prev.servings > 0 ? (totalCost / prev.servings).toFixed(2) : '0';
            return {
                ...prev,
                ingredients: newIngredients,
                cost_per_serving: newCostPerServing
            };
        });
    };

    const updateIngredient = (index, field, value) => {
        setFormData(prev => {
            const newIngredients = prev.ingredients.map((ing, i) => 
                i === index ? { ...ing, [field]: value } : ing
            );
            
            // Auto-recalculate cost_per_serving when estimated_cost changes
            if (field === 'estimated_cost') {
                const totalCost = newIngredients.reduce((sum, ing) => sum + (parseFloat(ing.estimated_cost) || 0), 0);
                const newCostPerServing = prev.servings > 0 ? (totalCost / prev.servings).toFixed(2) : '0';
                return {
                    ...prev,
                    ingredients: newIngredients,
                    cost_per_serving: newCostPerServing
                };
            }
            
            return {
                ...prev,
                ingredients: newIngredients
            };
        });
        
        // If updating name, search for matching recipes AND standardized ingredients
        if (field === 'name' && value.length >= 2) {
            searchRecipesForLinking(value, index);
            searchStandardizedIngredients(value, index);
        } else if (field === 'name') {
            setSuggestions([]);
            setIngredientSuggestions([]);
        }
    };
    
    // Search for standardized ingredient names
    const [ingredientSuggestions, setIngredientSuggestions] = useState([]);
    const [ingredientMatches, setIngredientMatches] = useState({}); // Track exact/similar matches per ingredient index
    
    const searchStandardizedIngredients = async (query, index) => {
        try {
            const response = await api.getStandardizedIngredients(query);
            if (activeIngredientIndex === index) {
                const suggestions = response.data.ingredients || [];
                setIngredientSuggestions(suggestions);
                
                // Check if current input matches an existing ingredient
                const queryLower = query.toLowerCase().trim();
                const exactMatch = suggestions.find(s => s.normalized === queryLower);
                const similarMatch = suggestions.length > 0 && !exactMatch ? suggestions[0] : null;
                
                setIngredientMatches(prev => ({
                    ...prev,
                    [index]: exactMatch ? { type: 'exact', ingredient: exactMatch } : 
                             similarMatch ? { type: 'similar', ingredient: similarMatch } : null
                }));
            }
        } catch (error) {
            console.error('Error searching ingredients:', error);
        }
    };
    
    // Select a standardized ingredient
    const selectStandardizedIngredient = (index, ingredient) => {
        setFormData(prev => ({
            ...prev,
            ingredients: prev.ingredients.map((ing, i) => 
                i === index ? { 
                    ...ing, 
                    name: ingredient.name,
                    unit: ingredient.common_unit || ing.unit
                } : ing
            )
        }));
        setIngredientSuggestions([]);
        // Mark as exact match after selection
        setIngredientMatches(prev => ({
            ...prev,
            [index]: { type: 'exact', ingredient }
        }));
    };

    // Step handlers
    const addStep = () => {
        setFormData(prev => ({
            ...prev,
            steps: [...prev.steps, '']
        }));
    };

    const removeStep = (index) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.filter((_, i) => i !== index)
        }));
    };

    const updateStep = (index, value) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.map((step, i) => i === index ? value : step)
        }));
    };

    // Calculate cost per serving based on ingredients
    const calculateCostPerServing = () => {
        const totalCost = formData.ingredients.reduce((sum, ing) => sum + (parseFloat(ing.estimated_cost) || 0), 0);
        return formData.servings > 0 ? (totalCost / formData.servings).toFixed(2) : 0;
    };

    // Solo tips management
    const addSoloTip = () => {
        setFormData(prev => ({ ...prev, solo_tips: [...prev.solo_tips, ''] }));
    };
    
    const removeSoloTip = (index) => {
        setFormData(prev => ({ 
            ...prev, 
            solo_tips: prev.solo_tips.filter((_, i) => i !== index) 
        }));
    };
    
    const updateSoloTip = (index, value) => {
        setFormData(prev => ({
            ...prev,
            solo_tips: prev.solo_tips.map((tip, i) => i === index ? value : tip)
        }));
    };

    // Variants management
    const addVariant = () => {
        setFormData(prev => ({ 
            ...prev, 
            variants: [...prev.variants, { 
                name: '', 
                description: '', 
                variant_type: 'topping',
                ingredients: [{ name: '', quantity: '', unit: '' }],
                instructions: '',
                extra_cost: 0,
                image_url: ''
            }] 
        }));
    };
    
    const removeVariant = (index) => {
        setFormData(prev => ({ 
            ...prev, 
            variants: prev.variants.filter((_, i) => i !== index) 
        }));
    };
    
    const updateVariant = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            variants: prev.variants.map((v, i) => i === index ? { ...v, [field]: value } : v)
        }));
    };
    
    const addVariantIngredient = (variantIndex) => {
        setFormData(prev => ({
            ...prev,
            variants: prev.variants.map((v, i) => 
                i === variantIndex 
                    ? { ...v, ingredients: [...(v.ingredients || []), { name: '', quantity: '', unit: '' }] }
                    : v
            )
        }));
    };
    
    const removeVariantIngredient = (variantIndex, ingIndex) => {
        setFormData(prev => ({
            ...prev,
            variants: prev.variants.map((v, i) => 
                i === variantIndex 
                    ? { ...v, ingredients: v.ingredients.filter((_, j) => j !== ingIndex) }
                    : v
            )
        }));
    };
    
    const updateVariantIngredient = (variantIndex, ingIndex, field, value) => {
        setFormData(prev => ({
            ...prev,
            variants: prev.variants.map((v, i) => 
                i === variantIndex 
                    ? { 
                        ...v, 
                        ingredients: v.ingredients.map((ing, j) => 
                            j === ingIndex ? { ...ing, [field]: value } : ing
                        ) 
                    }
                    : v
            )
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Filter out empty ingredients, steps, and solo tips
        const tagsArray = typeof formData.tags === 'string' 
            ? formData.tags.split(',').map(t => t.trim()).filter(Boolean)
            : (Array.isArray(formData.tags) ? formData.tags : []);
        
        const cleanedSoloTips = formData.solo_tips.filter(tip => tip.trim());
        
        // Clean variants - remove empty ones and empty ingredients within variants
        const cleanedVariants = formData.variants
            .filter(v => v.name.trim())
            .map(v => ({
                ...v,
                ingredients: (v.ingredients || []).filter(ing => ing.name.trim()),
                extra_cost: parseFloat(v.extra_cost) || 0
            }));
        
        const cleanedData = {
            ...formData,
            ingredients: formData.ingredients.filter(ing => ing.name.trim()),
            steps: formData.steps.filter(step => step.trim()),
            tags: tagsArray,
            cost_per_serving: parseFloat(formData.cost_per_serving) || parseFloat(calculateCostPerServing()),
            categories: formData.categories || [formData.category],
            solo_tips: cleanedSoloTips.length > 0 ? cleanedSoloTips : null,
            variants: cleanedVariants.length > 0 ? cleanedVariants : null,
            household_sizes: formData.household_sizes.length > 0 ? formData.household_sizes : null
        };
        onSave(cleanedData);
    };
    
    const toggleHouseholdSize = (size) => {
        setFormData(prev => {
            const current = prev.household_sizes || [];
            if (current.includes(size)) {
                return { ...prev, household_sizes: current.filter(s => s !== size) };
            } else {
                return { ...prev, household_sizes: [...current, size] };
            }
        });
    };

    const unitOptions = ['tasse', 'tasses', 'c. à soupe', 'c. à thé', 'ml', 'L', 'g', 'kg', 'lb', 'oz', 'pièce', 'pièces', 'gousse', 'gousses', 'tranche', 'tranches', 'pincée'];

    return (
        <form onSubmit={handleSubmit} className="space-y-4 border rounded-xl p-6 mb-6 bg-white">
            <div className="flex items-center justify-between pb-4 border-b">
                <h3 className="font-heading font-bold text-xl">{recipe?.id ? 'Modifier la Recette' : 'Nouvelle Recette'}</h3>
                <Button type="button" variant="ghost" size="sm" onClick={onCancel}><X className="w-4 h-4" /></Button>
            </div>
            
            {/* Quick navigation hint */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                📝 Ce formulaire contient: Informations de base → <strong>Ingrédients</strong> → <strong>Étapes de préparation</strong> → Options
            </div>

            {/* Basic Info */}
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <Label>Titre *</Label>
                    <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} required placeholder="Ex: Pâté chinois économique" />
                </div>
                <div>
                    <Label>Catégories (plusieurs possibles)</Label>
                    <div className="flex flex-wrap gap-1 mb-2 min-h-[32px]">
                        {(formData.categories || [formData.category]).map(cat => (
                            <Badge 
                                key={cat}
                                variant="secondary"
                                className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground text-xs"
                                onClick={() => {
                                    const cats = formData.categories || [formData.category];
                                    const newCats = cats.filter(c => c !== cat);
                                    if (newCats.length > 0) {
                                        setFormData(prev => ({ ...prev, categories: newCats, category: newCats[0] }));
                                    }
                                }}
                            >
                                {cat === 'breakfast' ? 'Déjeuner' : 
                                 cat === 'lunch' ? 'Dîner' : 
                                 cat === 'dinner' ? 'Souper' : 
                                 cat === 'soups' ? 'Soupes' : 
                                 cat === 'appetizers' ? 'Entrées' : 
                                 cat === 'salads' ? 'Salades' : 
                                 cat === 'sides' ? 'Accompagnements' : 
                                 cat === 'dessert' ? 'Dessert' : 
                                 cat === 'snack' ? 'Collation' : 
                                 cat === 'rescue' ? 'Recette secours' :
                                 cat === 'base' ? 'Les bases' :
                                 cat === 'airfryer' ? 'Friteuse à air' :
                                 cat === 'slowcooker' ? 'Mijoteuse' :
                                 cat === 'pressurecooker' ? 'Auto-cuiseur' :
                                 cat === 'bbq' ? 'BBQ' : cat} ×
                            </Badge>
                        ))}
                    </div>
                    <select 
                        className="w-full border rounded-md p-2" 
                        value=""
                        onChange={(e) => {
                            if (e.target.value) {
                                const cats = formData.categories || [formData.category];
                                if (!cats.includes(e.target.value)) {
                                    setFormData(prev => ({ 
                                        ...prev, 
                                        categories: [...cats, e.target.value],
                                        category: cats[0] || e.target.value
                                    }));
                                }
                            }
                        }}
                    >
                        <option value="">+ Ajouter une catégorie...</option>
                        <option value="breakfast">Déjeuner</option>
                        <option value="lunch">Dîner</option>
                        <option value="dinner">Souper</option>
                        <option value="soups">Soupes</option>
                        <option value="appetizers">Entrées</option>
                        <option value="salads">Salades</option>
                        <option value="sides">Accompagnements</option>
                        <option value="dessert">Dessert</option>
                        <option value="snack">Collation</option>
                        <option value="rescue">Recette secours</option>
                        <option value="base">Les bases</option>
                        <option value="airfryer">Friteuse à air</option>
                        <option value="slowcooker">Mijoteuse</option>
                        <option value="pressurecooker">Auto-cuiseur</option>
                        <option value="bbq">BBQ</option>
                    </select>
                </div>
            </div>

            <div>
                <Label>Description *</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} required placeholder="Décrivez brièvement la recette..." rows={2} />
            </div>

            {/* Time, Servings, Cost */}
            <div className="grid sm:grid-cols-4 gap-4">
                <div>
                    <Label>Préparation (min)</Label>
                    <Input type="number" value={formData.prep_time} onChange={(e) => setFormData(prev => ({ ...prev, prep_time: parseInt(e.target.value) || 0 }))} />
                </div>
                <div>
                    <Label>Cuisson (min)</Label>
                    <Input type="number" value={formData.cook_time} onChange={(e) => setFormData(prev => ({ ...prev, cook_time: parseInt(e.target.value) || 0 }))} />
                </div>
                <div>
                    <Label>Portions</Label>
                    <Input type="number" min="1" value={formData.servings} onChange={(e) => {
                        const newServings = parseInt(e.target.value) || 1;
                        setFormData(prev => {
                            const totalCost = prev.ingredients.reduce((sum, ing) => sum + (parseFloat(ing.estimated_cost) || 0), 0);
                            const newCostPerServing = newServings > 0 ? (totalCost / newServings).toFixed(2) : '0';
                            return { ...prev, servings: newServings, cost_per_serving: newCostPerServing };
                        });
                    }} />
                </div>
                <div>
                    <Label>Coût/portion ($)</Label>
                    <Input 
                        type="text" 
                        inputMode="decimal" 
                        placeholder="0.00"
                        value={formData.cost_per_serving} 
                        onChange={(e) => {
                            const value = e.target.value.replace(',', '.');
                            // Allow empty, numbers, and partial decimals like "3."
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                setFormData(prev => ({ ...prev, cost_per_serving: value }));
                            }
                        }}
                    />
                </div>
            </div>

            {/* INGREDIENTS SECTION */}
            <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
                <div className="flex items-center justify-between mb-4">
                    <Label className="text-lg font-bold flex items-center gap-2 text-green-700">
                        <ShoppingCart className="w-6 h-6" />
                        INGRÉDIENTS
                    </Label>
                    <Button type="button" variant="outline" size="sm" onClick={addIngredient} className="text-green-600 border-green-400">
                        <Plus className="w-4 h-4 mr-1" /> Ajouter un ingrédient
                    </Button>
                </div>
                
                <div className="space-y-3">
                    {formData.ingredients.map((ing, index) => (
                        <div key={index} className="bg-white p-3 rounded-lg border space-y-2">
                            <div className="flex items-center gap-2">
                                <div className="w-20">
                                    <Input 
                                        placeholder="Qté" 
                                        value={ing.quantity} 
                                        onChange={(e) => updateIngredient(index, 'quantity', e.target.value)}
                                    />
                                </div>
                                <div className="w-28">
                                    <select 
                                        className="w-full border rounded-md p-2 text-sm"
                                        value={ing.unit}
                                        onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                                    >
                                        {unitOptions.map(unit => (
                                            <option key={unit} value={unit}>{unit}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex-1 relative">
                                    <div className="relative">
                                        <Input 
                                            placeholder="Nom de l'ingrédient (tapez pour suggestions)" 
                                            value={ing.name} 
                                            onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                                            onFocus={() => setActiveIngredientIndex(index)}
                                            onBlur={() => setTimeout(() => setSuggestions([]), 200)}
                                            className={`pr-24 ${ing.linked_recipe_id ? 'border-purple-400 bg-purple-50' : 
                                                ingredientMatches[index]?.type === 'exact' ? 'border-green-400 bg-green-50' :
                                                ingredientMatches[index]?.type === 'similar' ? 'border-amber-400 bg-amber-50' : ''}`}
                                        />
                                        {/* Ingredient match indicator */}
                                        {ingredientMatches[index] && (
                                            <div className={`absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                                ingredientMatches[index].type === 'exact' 
                                                    ? 'bg-green-100 text-green-700' 
                                                    : 'bg-amber-100 text-amber-700'
                                            }`}>
                                                {ingredientMatches[index].type === 'exact' ? (
                                                    <>
                                                        <Check className="w-3 h-3" />
                                                        {ingredientMatches[index].ingredient.usage_count}x
                                                    </>
                                                ) : (
                                                    <>
                                                        <AlertCircle className="w-3 h-3" />
                                                        Similaire
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {/* Similar ingredient warning */}
                                    {ingredientMatches[index]?.type === 'similar' && activeIngredientIndex !== index && (
                                        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" />
                                            Orthographe similaire à "{ingredientMatches[index].ingredient.name}" ({ingredientMatches[index].ingredient.usage_count}x utilisé)
                                        </p>
                                    )}
                                    {/* Standardized Ingredients Suggestions */}
                                    {activeIngredientIndex === index && ingredientSuggestions.length > 0 && (
                                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border-2 border-green-300 rounded-lg shadow-lg overflow-hidden">
                                            <div className="px-3 py-2 bg-green-100 text-green-700 text-xs font-bold flex items-center gap-1">
                                                ✓ Ingrédients existants (cliquez pour uniformiser)
                                            </div>
                                            {ingredientSuggestions.slice(0, 5).map((ingredient, idx) => (
                                                <button
                                                    key={ingredient.normalized || idx}
                                                    type="button"
                                                    onClick={() => selectStandardizedIngredient(index, ingredient)}
                                                    className={`w-full px-3 py-2 text-left hover:bg-green-50 flex items-center justify-between border-t border-green-100 ${
                                                        ingredient.normalized === ing.name.toLowerCase().trim() ? 'bg-green-100' : ''
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        {ingredient.normalized === ing.name.toLowerCase().trim() && (
                                                            <Check className="w-4 h-4 text-green-600" />
                                                        )}
                                                        <span className="font-medium">{ingredient.name}</span>
                                                        {ingredient.common_unit && (
                                                            <span className="text-xs text-muted-foreground ml-1">
                                                                ({ingredient.common_unit})
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                                        ingredient.usage_count >= 5 ? 'bg-green-200 text-green-800' :
                                                        ingredient.usage_count >= 2 ? 'bg-blue-100 text-blue-700' :
                                                        'bg-gray-100 text-gray-600'
                                                    }`}>
                                                        {ingredient.usage_count}x
                                                    </span>
                                                </button>
                                            ))}
                                            {/* Also show recipe suggestions if available */}
                                            {suggestions.length > 0 && (
                                                <>
                                                    <div className="px-3 py-2 bg-purple-100 text-purple-700 text-xs font-bold flex items-center gap-1 border-t-2 border-purple-200">
                                                        <Crown className="w-3 h-3" />
                                                        Ou lier à une recette maison
                                                    </div>
                                                    {suggestions.slice(0, 3).map(suggestion => (
                                                        <button
                                                            key={suggestion.id}
                                                            type="button"
                                                            onClick={() => selectSuggestion(index, suggestion)}
                                                            className="w-full px-3 py-2 text-left hover:bg-purple-50 flex items-center gap-3 border-t border-purple-100"
                                                        >
                                                            {suggestion.image_url && (
                                                                <img 
                                                                    src={suggestion.image_url} 
                                                                    alt="" 
                                                                    className="w-8 h-8 rounded object-cover"
                                                                />
                                                            )}
                                                            <div className="flex-1">
                                                                <p className="font-medium text-foreground text-sm">{suggestion.title}</p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {suggestion.cost_per_serving?.toFixed(2)}$/portion
                                                                </p>
                                                            </div>
                                                            <Link2 className="w-4 h-4 text-purple-500" />
                                                        </button>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {/* Recipe-only Autocomplete dropdown (when no ingredient suggestions) */}
                                    {activeIngredientIndex === index && suggestions.length > 0 && ingredientSuggestions.length === 0 && (
                                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border-2 border-purple-300 rounded-lg shadow-lg overflow-hidden">
                                            <div className="px-3 py-2 bg-purple-100 text-purple-700 text-xs font-bold flex items-center gap-1">
                                                <Crown className="w-3 h-3" />
                                                Suggestions de recettes maison (Premium)
                                            </div>
                                            {suggestions.map(suggestion => (
                                                <button
                                                    key={suggestion.id}
                                                    type="button"
                                                    onClick={() => selectSuggestion(index, suggestion)}
                                                    className="w-full px-3 py-2 text-left hover:bg-purple-50 flex items-center gap-3 border-t border-purple-100"
                                                >
                                                    {suggestion.image_url && (
                                                        <img 
                                                            src={suggestion.image_url} 
                                                            alt="" 
                                                            className="w-10 h-10 rounded object-cover"
                                                        />
                                                    )}
                                                    <div className="flex-1">
                                                        <p className="font-medium text-foreground">{suggestion.title}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {suggestion.cost_per_serving?.toFixed(2)}$/portion
                                                        </p>
                                                    </div>
                                                    <Link2 className="w-4 h-4 text-purple-500" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {searchingRecipes && activeIngredientIndex === index && (
                                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg p-3 text-center text-sm text-muted-foreground">
                                            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                                            Recherche...
                                        </div>
                                    )}
                                </div>
                                <div className="w-24">
                                    <Input 
                                        type="text"
                                        inputMode="decimal"
                                        pattern="[0-9]*[.,]?[0-9]*"
                                        placeholder="Coût $" 
                                        value={ing.estimated_cost} 
                                        onChange={(e) => updateIngredient(index, 'estimated_cost', e.target.value.replace(',', '.'))}
                                    />
                                </div>
                                <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => removeIngredient(index)}
                                    className="text-destructive"
                                    disabled={formData.ingredients.length === 1}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                            {/* Linked recipe selector */}
                            {availableRecipes.length > 0 && (
                                <div className="flex items-center gap-2 pl-2">
                                    <Link2 className="w-4 h-4 text-purple-500" />
                                    <select
                                        className="flex-1 border rounded-md p-1.5 text-sm bg-purple-50 border-purple-200"
                                        value={ing.linked_recipe_id || ''}
                                        onChange={(e) => updateIngredient(index, 'linked_recipe_id', e.target.value)}
                                    >
                                        <option value="">Lier à une recette (optionnel)</option>
                                        {availableRecipes.map(r => (
                                            <option key={r.id} value={r.id}>{r.title}</option>
                                        ))}
                                    </select>
                                    {ing.linked_recipe_id && (
                                        <span className="text-xs text-purple-600 font-medium">Lié!</span>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                
                <div className="mt-3 pt-3 border-t flex justify-between text-sm">
                    <span>Coût total des ingrédients:</span>
                    <span className="font-bold text-green-600">
                        {formData.ingredients.reduce((sum, ing) => sum + (parseFloat(ing.estimated_cost) || 0), 0).toFixed(2)}$
                    </span>
                </div>
            </div>

            {/* STEPS SECTION */}
            <div className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
                <div className="flex items-center justify-between mb-4">
                    <Label className="text-lg font-bold flex items-center gap-2 text-blue-700">
                        <ChefHat className="w-6 h-6" />
                        ÉTAPES DE PRÉPARATION
                    </Label>
                    <Button type="button" variant="outline" size="sm" onClick={addStep} className="text-blue-600 border-blue-400">
                        <Plus className="w-4 h-4 mr-1" /> Ajouter une étape
                    </Button>
                </div>
                
                <div className="space-y-3">
                    {formData.steps.map((step, index) => (
                        <div key={index} className="flex items-start gap-3 bg-white p-3 rounded-lg border">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-blue-600">
                                {index + 1}
                            </div>
                            <div className="flex-1">
                                <Textarea 
                                    placeholder={`Étape ${index + 1}: Décrivez cette étape...`}
                                    value={step} 
                                    onChange={(e) => updateStep(index, e.target.value)}
                                    rows={2}
                                />
                            </div>
                            <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => removeStep(index)}
                                className="text-destructive"
                                disabled={formData.steps.length === 1}
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Image, Difficulty, Tags */}
            <div className="space-y-4">
                <ImageSelector
                    value={formData.image_url}
                    onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                    label="Photo de la recette"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Difficulté</Label>
                        <select className="w-full border rounded-md p-2" value={formData.difficulty} onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value }))}>
                            <option value="facile">Facile</option>
                            <option value="moyen">Moyen</option>
                            <option value="difficile">Difficile</option>
                        </select>
                    </div>
                    <div>
                        <Label>Type de protéine</Label>
                        <select className="w-full border rounded-md p-2" value={formData.protein_type} onChange={(e) => setFormData(prev => ({ ...prev, protein_type: e.target.value }))}>
                            <option value="">Non spécifié</option>
                            <option value="poulet">🍗 Poulet</option>
                            <option value="boeuf">🥩 Bœuf</option>
                            <option value="porc">🥓 Porc</option>
                            <option value="poisson">🐟 Poisson</option>
                            <option value="vegetarien">🥗 Végétarien</option>
                        </select>
                    </div>
                </div>
                
                {/* Freezable option */}
                <div className="flex items-center gap-3 mt-4 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                    <input 
                        type="checkbox" 
                        id="freezable" 
                        checked={formData.freezable} 
                        onChange={(e) => setFormData(prev => ({ ...prev, freezable: e.target.checked }))}
                        className="w-5 h-5 text-cyan-600 rounded"
                    />
                    <Label htmlFor="freezable" className="text-cyan-700 font-medium flex items-center gap-2 cursor-pointer">
                        <span className="text-lg">❄️</span>
                        Se congèle bien
                        <span className="text-xs text-cyan-500 font-normal">(cette recette est adaptée à la congélation)</span>
                    </Label>
                </div>
            </div>

            <div>
                <Label>Tags (séparés par virgules)</Label>
                <Input value={formData.tags} onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))} placeholder="économique, rapide, végétarien" />
            </div>

            {/* Astuce de la recette */}
            <div className="border-2 border-dashed border-yellow-300 rounded-xl p-4 bg-yellow-50/50">
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">💡</span>
                    <div>
                        <Label className="text-yellow-700 font-bold">Astuce du chef</Label>
                        <p className="text-xs text-yellow-600">Un conseil pour réussir cette recette (optionnel)</p>
                    </div>
                </div>
                <Textarea
                    value={formData.tip}
                    onChange={(e) => setFormData(prev => ({ ...prev, tip: e.target.value }))}
                    placeholder="Ex: Pour un poulet plus croustillant, laissez-le sécher au frigo pendant 1h avant la cuisson..."
                    className="min-h-[80px] rounded-lg border-yellow-200 bg-white"
                    rows={3}
                />
            </div>

            {/* Source/Crédit de la recette */}
            <div className="border-2 border-dashed border-blue-200 rounded-xl p-4 bg-blue-50/50">
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">📚</span>
                    <div>
                        <Label className="text-blue-700 font-bold">Source de la recette</Label>
                        <p className="text-xs text-blue-600">Si la recette vient d{"'"}ailleurs, indiquez la source (optionnel)</p>
                    </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-sm text-blue-600">Nom de la source</Label>
                        <Input
                            value={formData.source_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, source_name: e.target.value }))}
                            placeholder="Ex: Ricardo, Trois fois par jour..."
                            className="border-blue-200 bg-white"
                        />
                    </div>
                    <div>
                        <Label className="text-sm text-blue-600">Lien URL (optionnel)</Label>
                        <Input
                            value={formData.source_url}
                            onChange={(e) => setFormData(prev => ({ ...prev, source_url: e.target.value }))}
                            placeholder="https://..."
                            className="border-blue-200 bg-white"
                        />
                    </div>
                </div>
            </div>

            {/* Cuisiner en solo section */}
            <div className="border-2 border-dashed border-orange-200 rounded-xl p-4 bg-orange-50/50">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <span className="text-2xl">👤</span>
                        <div>
                            <Label className="text-orange-700 font-bold">Cuisiner en solo</Label>
                            <p className="text-xs text-orange-600">Conseils pour les personnes seules</p>
                        </div>
                    </div>
                    <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={addSoloTip}
                        className="text-orange-600 border-orange-300 hover:bg-orange-100"
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        Ajouter
                    </Button>
                </div>
                {formData.solo_tips.length > 0 && formData.solo_tips.some(t => t || formData.solo_tips.length === 1) ? (
                    <div className="space-y-2">
                        {formData.solo_tips.map((tip, index) => (
                            <div key={index} className="flex gap-2">
                                <span className="text-orange-500 font-bold mt-2">💡</span>
                                <Textarea
                                    value={tip}
                                    onChange={(e) => updateSoloTip(index, e.target.value)}
                                    placeholder="Ex: Divisez la recette par 2 et congelez le reste, ou préparez pour 2 jours..."
                                    className="flex-1 min-h-[60px] rounded-lg border-orange-200"
                                    rows={2}
                                />
                                <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => removeSoloTip(index)}
                                    className="text-orange-400 hover:text-orange-600 h-8 w-8 mt-1"
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-orange-500 italic text-center py-2">
                        Aucun conseil solo pour cette recette
                    </p>
                )}
            </div>

            {/* Variants Section */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border-2 border-purple-200">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                            <span className="text-white text-lg">🎨</span>
                        </div>
                        <div>
                            <Label className="text-purple-800 font-bold">Variantes de la recette</Label>
                            <p className="text-xs text-purple-600">Garnitures alternatives, versions végétariennes, etc.</p>
                        </div>
                    </div>
                    <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={addVariant}
                        className="text-purple-600 border-purple-300 hover:bg-purple-100"
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        Ajouter une variante
                    </Button>
                </div>
                
                {formData.variants.length > 0 ? (
                    <div className="space-y-4">
                        {formData.variants.map((variant, vIndex) => (
                            <div key={vIndex} className="bg-white rounded-xl p-4 border border-purple-200 space-y-3">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 grid sm:grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs text-purple-600">Nom de la variante *</Label>
                                            <Input
                                                value={variant.name}
                                                onChange={(e) => updateVariant(vIndex, 'name', e.target.value)}
                                                placeholder="Ex: Garniture bacon-cheddar"
                                                className="border-purple-200"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-purple-600">Type</Label>
                                            <select
                                                value={variant.variant_type}
                                                onChange={(e) => updateVariant(vIndex, 'variant_type', e.target.value)}
                                                className="w-full border border-purple-200 rounded-md px-3 py-2 text-sm"
                                            >
                                                <option value="topping">🍳 Garniture</option>
                                                <option value="alternative">🔄 Alternative (végé, sans gluten...)</option>
                                                <option value="suggestion">💡 Suggestion</option>
                                            </select>
                                        </div>
                                    </div>
                                    <Button 
                                        type="button" 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => removeVariant(vIndex)}
                                        className="text-red-400 hover:text-red-600 h-8 w-8"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                                
                                <div>
                                    <Label className="text-xs text-purple-600">Description</Label>
                                    <Textarea
                                        value={variant.description || ''}
                                        onChange={(e) => updateVariant(vIndex, 'description', e.target.value)}
                                        placeholder="Brève description de cette variante..."
                                        className="border-purple-200 min-h-[60px]"
                                        rows={2}
                                    />
                                </div>
                                
                                {/* Variant Ingredients */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <Label className="text-xs text-purple-600">Ingrédients de la variante</Label>
                                        <Button 
                                            type="button" 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={() => addVariantIngredient(vIndex)}
                                            className="text-purple-600 h-6 text-xs"
                                        >
                                            <Plus className="w-3 h-3 mr-1" />
                                            Ajouter
                                        </Button>
                                    </div>
                                    <div className="space-y-2">
                                        {(variant.ingredients || []).map((ing, iIndex) => (
                                            <div key={iIndex} className="flex gap-2 items-center">
                                                <Input
                                                    value={ing.quantity}
                                                    onChange={(e) => updateVariantIngredient(vIndex, iIndex, 'quantity', e.target.value)}
                                                    placeholder="Qté"
                                                    className="w-16 border-purple-200 text-sm"
                                                />
                                                <Input
                                                    value={ing.unit || ''}
                                                    onChange={(e) => updateVariantIngredient(vIndex, iIndex, 'unit', e.target.value)}
                                                    placeholder="Unité"
                                                    className="w-20 border-purple-200 text-sm"
                                                />
                                                <Input
                                                    value={ing.name}
                                                    onChange={(e) => updateVariantIngredient(vIndex, iIndex, 'name', e.target.value)}
                                                    placeholder="Nom de l'ingrédient"
                                                    className="flex-1 border-purple-200 text-sm"
                                                />
                                                <Button 
                                                    type="button" 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => removeVariantIngredient(vIndex, iIndex)}
                                                    className="text-gray-400 hover:text-red-500 h-7 w-7"
                                                >
                                                    <X className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                <div className="grid sm:grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs text-purple-600">Instructions spéciales</Label>
                                        <Textarea
                                            value={variant.instructions || ''}
                                            onChange={(e) => updateVariant(vIndex, 'instructions', e.target.value)}
                                            placeholder="Instructions pour préparer cette variante..."
                                            className="border-purple-200 min-h-[60px]"
                                            rows={2}
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-purple-600">Coût additionnel ($)</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={variant.extra_cost || ''}
                                            onChange={(e) => updateVariant(vIndex, 'extra_cost', e.target.value)}
                                            placeholder="0.00"
                                            className="border-purple-200"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-purple-500 italic text-center py-4 bg-white rounded-lg">
                        Aucune variante. Ajoutez des garnitures ou alternatives pour enrichir votre recette!
                    </p>
                )}
            </div>

            {/* Household Sizes Section */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border-2 border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                        <span className="text-white text-lg">👥</span>
                    </div>
                    <div>
                        <Label className="text-blue-800 font-bold">Pour qui est cette recette?</Label>
                        <p className="text-xs text-blue-600">Sélectionnez les types de foyers adaptés</p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {householdSizeOptions.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => toggleHouseholdSize(option.value)}
                            className={`px-4 py-2 rounded-xl border-2 transition-all font-medium text-sm ${
                                (formData.household_sizes || []).includes(option.value)
                                    ? 'border-blue-500 bg-blue-500 text-white shadow-lg'
                                    : 'border-blue-200 bg-white hover:border-blue-400 text-blue-800'
                            }`}
                        >
                            <span className="mr-1">{option.icon}</span>
                            {option.label}
                        </button>
                    ))}
                </div>
                {(formData.household_sizes || []).length === 0 && (
                    <p className="text-xs text-blue-500 mt-2 italic">
                        💡 Aucune sélection = visible pour tous les types de foyers
                    </p>
                )}
            </div>

            <div className="flex items-center gap-2">
                <input type="checkbox" id="is_premium" checked={formData.is_premium} onChange={(e) => setFormData(prev => ({ ...prev, is_premium: e.target.checked }))} />
                <Label htmlFor="is_premium">Recette Premium</Label>
            </div>

            <div className="flex gap-3 justify-end sticky bottom-0 bg-white pt-4 border-t">
                <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
                <Button type="submit" className="bg-primary text-white"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
            </div>
        </form>
    );
};

const TipForm = ({ tip, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        title: tip?.title || '',
        content: tip?.content || '',
        category: tip?.category || 'budget',
        savings_estimate: tip?.savings_estimate || '',
        is_premium: tip?.is_premium || false,
        ...(tip?.id && { id: tip.id })
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6 border rounded-xl p-6 mb-6 bg-white">
            <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-xl">{tip?.id ? 'Modifier Astuce' : 'Nouvelle Astuce'}</h3>
                <Button type="button" variant="ghost" size="sm" onClick={onCancel}><X className="w-4 h-4" /></Button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <Label>Titre</Label>
                    <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} required />
                </div>
                <div>
                    <Label>Catégorie</Label>
                    <select className="w-full border rounded-md p-2" value={formData.category} onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}>
                        <option value="shopping">Courses</option>
                        <option value="storage">Conservation</option>
                        <option value="cooking">Cuisine</option>
                        <option value="budget">Budget</option>
                    </select>
                </div>
            </div>

            <div>
                <Label>Contenu</Label>
                <Textarea value={formData.content} onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))} rows={4} required />
            </div>

            <div>
                <Label>Économies estimées</Label>
                <Input value={formData.savings_estimate} onChange={(e) => setFormData(prev => ({ ...prev, savings_estimate: e.target.value }))} placeholder="Ex: 30% sur les courses" />
            </div>

            <div className="flex items-center gap-2">
                <input type="checkbox" id="tip_premium" checked={formData.is_premium} onChange={(e) => setFormData(prev => ({ ...prev, is_premium: e.target.checked }))} />
                <Label htmlFor="tip_premium">Astuce Premium</Label>
            </div>

            <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
                <Button type="submit" className="bg-primary text-white"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
            </div>
        </form>
    );
};

const DealForm = ({ deal, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        product_name: deal?.product_name || '',
        store_name: deal?.store_name || '',
        original_price: deal?.original_price || '',
        sale_price: deal?.sale_price || '',
        discount_percent: deal?.discount_percent || 0,
        category: deal?.category || 'pantry',
        image_url: deal?.image_url || '',
        valid_until: deal?.valid_until || '',
        ...(deal?.id && { id: deal.id })
    });

    // Calculate discount on price change
    const originalNum = parseFloat(formData.original_price) || 0;
    const saleNum = parseFloat(formData.sale_price) || 0;
    const calculatedDiscount = originalNum > 0 && saleNum > 0
        ? Math.round((1 - saleNum / originalNum) * 100)
        : formData.discount_percent;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({ 
            ...formData, 
            original_price: parseFloat(formData.original_price) || 0,
            sale_price: parseFloat(formData.sale_price) || 0,
            discount_percent: calculatedDiscount 
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6 border rounded-xl p-6 mb-6 bg-white">
            <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-xl">{deal?.id ? 'Modifier Rabais' : 'Nouveau Rabais'}</h3>
                <Button type="button" variant="ghost" size="sm" onClick={onCancel}><X className="w-4 h-4" /></Button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <Label>Produit</Label>
                    <Input value={formData.product_name} onChange={(e) => setFormData(prev => ({ ...prev, product_name: e.target.value }))} required />
                </div>
                <div>
                    <Label>Magasin</Label>
                    <Input value={formData.store_name} onChange={(e) => setFormData(prev => ({ ...prev, store_name: e.target.value }))} required />
                </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
                <div>
                    <Label>Prix original ($)</Label>
                    <Input 
                        type="text" 
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        placeholder="0.00"
                        value={formData.original_price} 
                        onChange={(e) => {
                            const value = e.target.value.replace(',', '.');
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                setFormData(prev => ({ ...prev, original_price: value }));
                            }
                        }} 
                        required 
                    />
                </div>
                <div>
                    <Label>Prix rabais ($)</Label>
                    <Input 
                        type="text" 
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        placeholder="0.00"
                        value={formData.sale_price} 
                        onChange={(e) => {
                            const value = e.target.value.replace(',', '.');
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                setFormData(prev => ({ ...prev, sale_price: value }));
                            }
                        }} 
                        required 
                    />
                </div>
                <div>
                    <Label>Rabais (%)</Label>
                    <Input type="number" value={calculatedDiscount} readOnly className="bg-muted" />
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <Label>Catégorie</Label>
                    <select className="w-full border rounded-md p-2" value={formData.category} onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}>
                        <option value="fruits">Fruits</option>
                        <option value="vegetables">Légumes</option>
                        <option value="meat">Viandes</option>
                        <option value="dairy">Produits laitiers</option>
                        <option value="pantry">Épicerie</option>
                        <option value="frozen">Surgelés</option>
                    </select>
                </div>
                <div>
                    <Label>Valide jusqu&apos;au</Label>
                    <Input type="date" value={formData.valid_until} onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))} />
                </div>
            </div>

            <ImageSelector
                value={formData.image_url}
                onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                label="Image du produit"
            />

            <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
                <Button type="submit" className="bg-primary text-white"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
            </div>
        </form>
    );
};

// ==================== DOSSIER COMPONENTS ====================

const DossierItem = ({ dossier, onEdit, onDelete }) => (
    <div className="flex items-center justify-between p-4 rounded-xl mb-2 bg-purple-50 border border-purple-200">
        <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
                <Badge className={dossier.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                    {dossier.is_published ? 'Publié' : 'Brouillon'}
                </Badge>
                <span className="font-semibold">{dossier.title}</span>
                <Crown className="w-4 h-4 text-purple-500" />
            </div>
            <p className="text-sm text-muted-foreground">
                /{dossier.slug} • {dossier.featured_recipes?.length || 0} recettes • Créé le {new Date(dossier.created_at).toLocaleDateString('fr-CA')}
            </p>
        </div>
        <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onEdit}><Edit className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
        </div>
    </div>
);

const DossierForm = ({ dossier, recipes, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        title: dossier?.title || '',
        slug: dossier?.slug || '',
        cover_image: dossier?.cover_image || '',
        content: dossier?.content || '',
        video_url: dossier?.video_url || '',
        featured_recipes: dossier?.featured_recipes || [],
        is_published: dossier?.is_published || false,
        ...dossier
    });

    const [showRecipeSelector, setShowRecipeSelector] = useState(false);
    const [recipeSearch, setRecipeSearch] = useState('');

    // Auto-generate slug from title
    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
    };

    const handleTitleChange = (e) => {
        const title = e.target.value;
        setFormData(prev => ({
            ...prev,
            title,
            slug: dossier?.slug || generateSlug(title)
        }));
    };

    const toggleRecipe = (recipeId) => {
        setFormData(prev => ({
            ...prev,
            featured_recipes: prev.featured_recipes.includes(recipeId)
                ? prev.featured_recipes.filter(id => id !== recipeId)
                : [...prev.featured_recipes, recipeId]
        }));
    };

    const insertRecipeLink = (recipe) => {
        const linkSyntax = `[[recipe:${recipe.id}|${recipe.title}]]`;
        setFormData(prev => ({
            ...prev,
            content: prev.content + ' ' + linkSyntax
        }));
        toast.success(`Lien vers "${recipe.title}" inséré`);
    };

    const filteredRecipes = recipes.filter(r => 
        r.title.toLowerCase().includes(recipeSearch.toLowerCase())
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const selectedRecipes = recipes.filter(r => formData.featured_recipes.includes(r.id));

    return (
        <form onSubmit={handleSubmit} className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6 mb-4 space-y-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading text-xl font-bold text-purple-800">
                    {dossier?.id ? 'Modifier le dossier' : 'Nouveau dossier spécial'}
                </h3>
                <Button type="button" variant="ghost" size="sm" onClick={onCancel}><X className="w-4 h-4" /></Button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <Label>Titre *</Label>
                    <Input 
                        value={formData.title} 
                        onChange={handleTitleChange}
                        placeholder="Ex: Spécial Boulettes"
                        required
                    />
                </div>
                <div>
                    <Label>Slug (URL)</Label>
                    <Input 
                        value={formData.slug} 
                        onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                        placeholder="special-boulettes"
                    />
                </div>
            </div>

            <div>
                <ImageSelector
                    value={formData.cover_image}
                    onChange={(url) => setFormData(prev => ({ ...prev, cover_image: url }))}
                    label="Image de couverture"
                />
            </div>

            <div>
                <Label>URL Vidéo YouTube (optionnel)</Label>
                <Input 
                    value={formData.video_url} 
                    onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=..."
                />
            </div>

            <div>
                <div className="flex justify-between items-center mb-2">
                    <Label>Contenu</Label>
                    <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowRecipeSelector(!showRecipeSelector)}
                        className="text-purple-600"
                    >
                        <Link2 className="w-4 h-4 mr-1" />
                        Insérer lien recette
                    </Button>
                </div>
                
                {showRecipeSelector && (
                    <div className="bg-white border rounded-lg p-3 mb-2 max-h-48 overflow-y-auto">
                        <Input 
                            placeholder="Rechercher une recette..."
                            value={recipeSearch}
                            onChange={(e) => setRecipeSearch(e.target.value)}
                            className="mb-2"
                        />
                        <div className="space-y-1">
                            {filteredRecipes.slice(0, 8).map(recipe => (
                                <button
                                    key={recipe.id}
                                    type="button"
                                    onClick={() => insertRecipeLink(recipe)}
                                    className="w-full text-left p-2 hover:bg-purple-50 rounded text-sm flex items-center gap-2"
                                >
                                    <Link2 className="w-3 h-3 text-purple-500" />
                                    {recipe.title}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                
                <Textarea 
                    value={formData.content} 
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Écrivez votre article ici...

Pour insérer un lien vers une recette, utilisez le bouton ci-dessus ou écrivez: [[recipe:ID|texte affiché]]"
                    className="min-h-[200px] font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                    Syntaxe des liens: [[recipe:ID|texte affiché]] - Utilisez le bouton pour insérer facilement
                </p>
            </div>

            <div>
                <Label className="mb-2 block">Recettes associées (affichées en bas du dossier)</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                    {selectedRecipes.map(recipe => (
                        <Badge 
                            key={recipe.id}
                            variant="secondary"
                            className="bg-purple-100 text-purple-700 cursor-pointer hover:bg-purple-200"
                            onClick={() => toggleRecipe(recipe.id)}
                        >
                            {recipe.title}
                            <X className="w-3 h-3 ml-1" />
                        </Badge>
                    ))}
                </div>
                <select 
                    className="w-full border rounded-md p-2"
                    value=""
                    onChange={(e) => {
                        if (e.target.value) toggleRecipe(e.target.value);
                    }}
                >
                    <option value="">+ Ajouter une recette...</option>
                    {recipes.filter(r => !formData.featured_recipes.includes(r.id)).map(recipe => (
                        <option key={recipe.id} value={recipe.id}>{recipe.title}</option>
                    ))}
                </select>
            </div>

            <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    id="is_published"
                    checked={formData.is_published}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                    className="w-4 h-4"
                />
                <Label htmlFor="is_published" className="cursor-pointer">Publier ce dossier</Label>
            </div>

            <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
                <Button type="submit" className="bg-purple-600 text-white hover:bg-purple-700">
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer
                </Button>
            </div>
        </form>
    );
};

// AI Generator Modal Component
const AIGeneratorModal = ({ type, items, loading, onGenerate, onSelect, onClose, error }) => {
    const isRecipe = type === 'recipe';
    const title = isRecipe ? 'Générer des Recettes avec IA' : 'Générer des Astuces avec IA';
    const subtitle = isRecipe 
        ? "L'IA va générer 7 recettes économiques québécoises variées" 
        : "L'IA va générer 7 astuces économiques pour le Québec";
    
    const categoryLabels = {
        breakfast: 'Déjeuner',
        lunch: 'Dîner',
        dinner: 'Souper',
        soups: 'Soupes',
        appetizers: 'Entrées',
        salads: 'Salades',
        sides: 'Accompagnements',
        snack: 'Collation',
        dessert: 'Dessert',
        rescue: 'Recette secours',
        base: 'Les bases',
        airfryer: 'Friteuse à air',
        slowcooker: 'Mijoteuse',
        pressurecooker: 'Auto-cuiseur',
        bbq: 'BBQ',
        shopping: 'Courses',
        storage: 'Conservation',
        cooking: 'Cuisine',
        budget: 'Budget'
    };

    return (
        <div className="border-2 border-purple-200 rounded-xl p-6 mb-6 bg-gradient-to-br from-purple-50 to-violet-50">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="font-heading font-bold text-xl text-purple-900">{title}</h3>
                        <p className="text-sm text-purple-600">{subtitle}</p>
                    </div>
                </div>
                <Button variant="ghost" size="sm" onClick={onClose}>
                    <X className="w-5 h-5" />
                </Button>
            </div>

            {/* Budget Error Message */}
            {error === 'budget' && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="w-8 h-8 text-red-500" />
                    </div>
                    <h4 className="font-heading font-bold text-lg text-red-800 mb-2">
                        Budget IA dépassé
                    </h4>
                    <p className="text-red-600 mb-4">
                        {"Votre limite de budget pour l'IA a été atteinte."}
                    </p>
                    <div className="bg-white rounded-lg p-4 text-left text-sm text-gray-600">
                        <p className="font-medium text-gray-800 mb-2">Pour continuer à utiliser cette fonctionnalité :</p>
                        <ol className="list-decimal list-inside space-y-1">
                            <li>Cliquez sur votre <strong>icône de profil</strong> (en haut à droite)</li>
                            <li>Sélectionnez <strong>Universal Key</strong></li>
                            <li>Augmentez votre <strong>solde</strong> ou activez la <strong>recharge automatique</strong></li>
                        </ol>
                    </div>
                    <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={onClose}
                    >
                        Fermer
                    </Button>
                </div>
            )}

            {/* Generic Error Message */}
            {error === 'generic' && (
                <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 text-center">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="w-8 h-8 text-orange-500" />
                    </div>
                    <h4 className="font-heading font-bold text-lg text-orange-800 mb-2">
                        Erreur de génération
                    </h4>
                    <p className="text-orange-600 mb-4">
                        {"Une erreur s'est produite lors de la génération. Veuillez réessayer."}
                    </p>
                    <Button 
                        onClick={onGenerate}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                        <Wand2 className="w-4 h-4 mr-2" />
                        Réessayer
                    </Button>
                </div>
            )}

            {/* Generate Button */}
            {items.length === 0 && !loading && !error && (
                <div className="text-center py-8">
                    <Button 
                        onClick={onGenerate}
                        className="bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                    >
                        <Wand2 className="w-5 h-5 mr-2" />
                        Générer maintenant
                    </Button>
                    <p className="text-sm text-gray-500 mt-3">Cela peut prendre 10-20 secondes</p>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="text-center py-12">
                    <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
                    <p className="text-purple-700 font-medium">Génération en cours...</p>
                    <p className="text-sm text-purple-500">{"L'IA prépare vos suggestions"}</p>
                </div>
            )}

            {/* Results */}
            {items.length > 0 && !loading && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-purple-700">
                            {items.length} {isRecipe ? 'recettes' : 'astuces'} générées - Cliquez pour modifier et publier
                        </p>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={onGenerate}
                            className="border-purple-300 text-purple-600 hover:bg-purple-50"
                        >
                            <Wand2 className="w-4 h-4 mr-1" />
                            Régénérer
                        </Button>
                    </div>
                    
                    <div className="grid gap-3 max-h-[400px] overflow-y-auto pr-2">
                        {items.map((item, index) => (
                            <div 
                                key={index}
                                onClick={() => onSelect(item)}
                                className="bg-white border border-purple-100 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer group"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h4 className="font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">
                                                {item.title}
                                            </h4>
                                            <Badge className="bg-purple-100 text-purple-700 text-xs">
                                                {categoryLabels[item.category] || item.category}
                                            </Badge>
                                        </div>
                                        <p className="text-sm text-gray-600 line-clamp-2">
                                            {isRecipe ? item.description : item.content}
                                        </p>
                                        {isRecipe && (
                                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {item.prep_time + item.cook_time} min
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <DollarSign className="w-3 h-3" />
                                                    {item.cost_per_serving?.toFixed(2)}$/portion
                                                </span>
                                                <span>{item.servings} portions</span>
                                            </div>
                                        )}
                                        {!isRecipe && item.savings_estimate && (
                                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                                <DollarSign className="w-3 h-3" />
                                                Économies: {item.savings_estimate}
                                            </p>
                                        )}
                                    </div>
                                    <div className="ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <div className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                            <Edit className="w-3 h-3" />
                                            Modifier
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPage;
